import ShopvibesPushModal, {
  type ShopvibesPushItem,
} from "../components/shopvibes/ShopvibesPushModal.tsx";
import ImageLinkListModal from "../components/shopvibes/ImageLinkListModal.tsx";
import { studioExportSelection } from "../utils/studioExportSelection.ts";
import AssetExportMenu from "../components/AssetExportMenu.tsx";
import { useUserSettings } from "../hooks/useUserSettings.ts";
import { MeasurementImportDialog } from "../components/MeasurementImportDialog";
import { useProductMeasurements } from "../hooks/useProductMeasurements";
import type { ExcelRow } from "../utils/excelDimensionImport";
import { mergeGeneratedImageSettings } from "../utils/imageGenerationResult";
import ImageGenerationButton from "../components/ImageGenerationSettings";
import type { ImageGenerationSettings } from "../../functions/src/shared/imageGeneration";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { auth } from "@/src/config/firebase";
import { useProjectContext } from "@/src/context/ProjectContext.tsx";
import StudioPanel from "@/src/components/StudioPanel.tsx";
import PanelErrorBoundary from "@/src/components/PanelErrorBoundary.tsx";
import { StudioProductCard } from "@/src/components/StudioProductCard.tsx";
import MultiProductSceneConfig from "@/src/components/MultiProductSceneConfig.tsx";
import GenerateButtonDropdown from "@/src/components/GenerateButtonDropdown.tsx";
import WorkflowRunButton from "../components/WorkflowRunButton";
import type { Workflow } from "../types";

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  CursorArrowRaysIcon,
  PencilSquareIcon,
  PhotoIcon,
  SparklesIcon,
  Squares2X2Icon,
  TrashIcon,
} from "@/src/components/icons.tsx";
import {
  GeneratedContent,
  DimensionMarker,
  MultiProductScene,
  ProductDetail,
  ProductRole,
  StudioProduct,
  WorkflowNode,
  ColorSwatch,
  WorkflowEdge,
} from "@/src/types.ts";
import {
  getImageSrc,
  inferRotationDefaultMode,
  inferRotationDefaultModeAsync,
  serializeGenerationSettings,
} from "@/src/utils/imageUtils.ts";
import {
  createSizeComparisonCanvas,
  type SizeComparisonCanvasDebug,
} from "@/src/utils/studioRouteHelpers.ts";
import { DEBUG_TOOLS_ENABLED } from "@/src/utils/debugFlags.ts";
import { downloadMultiProductDebugReport } from "@/src/utils/multiProductDebugReport.ts";
import {
  buildPromptProductName,
  buildPromptProductNames,
} from "@/src/utils/multiProductPromptName.ts";
import * as geminiService from "@/src/services/geminiService.ts";
import { executeBackgroundRemoval } from "@/src/services/imageGenerationEngine.ts";
import { setGenerationContext } from "@/src/services/backendAiService.ts";

import { generateUniqueFilename } from "@/src/utils/helpers.ts";
import { COST_BG_REMOVE, COST_DIMENSION } from "@/src/utils/constants.ts";
import {
  MAX_MULTI_PRODUCT_SCENE_PRODUCTS,
  canSelectMultiProductSceneProduct,
  exceedsMultiProductSceneLimit,
} from "@/src/utils/multiProductLimits.ts";
import { resolveBackgroundRemovalConfig } from "@/src/utils/backgroundRemoval.ts";
import FileSizeWarningModal from "@/src/components/FileSizeWarningModal.tsx";
import UploadMenu from "@/src/components/UploadMenu.tsx";
import ExcelImportErrorDialog, {
  type ExcelImportErrorState,
} from "@/src/components/ExcelImportErrorDialog.tsx";
import {
  MAX_SHEET_FILE_BYTES,
  SPREADSHEET_FILENAME_RE,
  XLSX_FILENAME_RE,
} from "@/src/utils/excelDimensionImport.ts";
import {
  persistStudioProduct,
  persistGeneratedContent,
  deleteStudioProduct,
  persistMultiProductScene,
  updateMultiProductSceneConfig,
  updateMultiProductSceneLinks,
  deleteMultiProductScene,
  uploadFileAsProduct,
} from "@/src/services/studioPersistenceService.ts";
import {
  parseProductDimensionCm,
  scalingDimensionsComplete,
} from "@/src/utils/dimensionUtils.ts";
import { environmentIdSettings } from "@/src/utils/regeneration.ts";
import { resolveEnvironmentReference } from "@/src/utils/environmentReference.ts";
import { resolveErrorMessage } from "@/src/utils/errorResolver.ts";
import { AppError } from "@/src/utils/appError.ts";
import {
  reportError,
  sanitizeFileNameForReport,
} from "@/src/utils/errorReporting.ts";
import { useAutoSaveProduct } from "@/src/hooks/useAutoSaveProduct.ts";
import { useProductAnalysis } from "@/src/hooks/useProductUpload.ts";
import {
  useGetGoalGalleryEligibility,
  useListUserWorkflows,
} from "@dataconnect/generated/react";
import {
  trackImageUploaded,
  trackProductDeleted,
  trackProductMeasurementsImportAttempt,
  trackQuickstartCompleted,
  trackQuickstartSkipped,
  trackQuickstartStarted,
  trackQuickstartStageReached,
  trackCreationGoalSelected,
  type QuickstartStage,
} from "@/src/services/analyticsService";
import { useQuickStartTour } from "@/src/hooks/useQuickStartTour";
import { deriveQuickStartStage } from "@/src/utils/quickStartStage.ts";
import { flowForGoal } from "@/src/utils/quickStartFlow.ts";
import { resolveDeepLinkMode } from "@/src/utils/deepLinkMode.ts";
import {
  CreationGoalGrid,
  CREATION_ICONS,
} from "@/src/components/CreationModePicker.tsx";
import {
  findCreationEntry,
  type CreationCatalogEntry,
} from "@/src/config/creationCatalog.ts";
import { detectImageMimeType } from "@/src/utils/imageFileTypes.ts";
import QuickStartPrompt from "@/src/components/quickStart/QuickStartPrompt.tsx";

/** Caps the rejected-file list in the toast so it stays one readable line. */
const formatRejectedFileNames = (names: string[]): string =>
  names.length > 3
    ? `${names.slice(0, 3).join(", ")} (+${names.length - 3})`
    : names.join(", ");

const AutoSaveProductCard: React.FC<{
  product: StudioProduct;
  onSaveStateChange: (
    id: string,
    state: { isSaving?: boolean; lastSaved?: number; saveError?: string | null }
  ) => void;
  onSelectionChange: (id: string, selected: boolean) => void;
  onDimensionsChange: (
    id: string,
    updates: {
      width?: string;
      height?: string;
    }
  ) => void;
  onInfoChange: (id: string, info: string) => void;
  imageGeneration?: ImageGenerationSettings;
  requiresSunburst?: boolean;
  onImageGenerationChange?: (settings: ImageGenerationSettings) => void;
  workflow?: Workflow;
  onGenerate: (
    id: string,
    size: "1K" | "2K" | "4K",
    imageGeneration?: ImageGenerationSettings,
    workflow?: Workflow
  ) => void;
  onViewImage: (productId: string, contentId: string) => void;
  onDownloadImage: (url: string, name: string) => void;
  onDelete: (id: string) => void;
  onRetryBackgroundRemoval: (id: string) => void;
  studioImageType: string;
  onStartLinking: (id: string) => void;
  linkingState: any;
  onToggleLinkSelection: (id: string) => void;
  modelType: string;
  globalContextLength: number;
  onViewSourceImage: (id: string, initialTab?: "details") => void;
  colorSwatches: ColorSwatch[];
  onRetryProductAnalysis: (id: string) => void;
  cutoutOutputFormat?: "png" | "jpeg";
  dimensionMarker?: DimensionMarker;
}> = ({ product, onSaveStateChange, ...props }) => {
  useAutoSaveProduct(product, onSaveStateChange);
  return <StudioProductCard product={product} {...props} />;
};

const StudioView: React.FC = () => {
  const { t } = useTranslation();
  const {
    studioProducts,
    setStudioProducts,
    multiProductScenes,
    setMultiProductScenes,
    vibes,
    scenes,
    retailerProfiles,
    logoPosition,
    setLogoPosition,
    logoScale,
    setLogoScale,
    dimensionMarker,
    dimensionPresetId,
    setDimensionPresetId,
    activeDimensionPreset,
    setDimensionMarker,
    dimensionShowLabel,
    setDimensionShowLabel,
    dimensionCapStyle,
    setDimensionCapStyle,
    dimensionTargetFormat,
    setDimensionTargetFormat,
    companyLogo,
    setCompanyLogo,
    namingTemplate,
    useAvatar,
    setUseAvatar,
    savedColorSwatches,
    modelType,
    setError,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    studioVibe,
    setStudioVibe,
    studioScene,
    setStudioScene,
    studioImageType,
    setStudioImageType,
    studioImageGeneration,
    setStudioImageGeneration,
    studioAspectRatio,
    setStudioAspectRatio,
    studioVibeContext,
    setStudioVibeContext,
    useTrueToneStudio,
    setUseTrueToneStudio,
    productLightsOn,
    setProductLightsOn,
    productLightsWarmth,
    setProductLightsWarmth,
    studioDecorationSurfaceOverride,
    setStudioDecorationSurfaceOverride,
    studioDecorationFramingPreference,
    setStudioDecorationFramingPreference,
    studioDecorationRoomPlacementPreference,
    setStudioDecorationRoomPlacementPreference,
    studioDecorationViewAngle,
    setStudioDecorationViewAngle,
    studioSelectedProfileId,
    setStudioSelectedProfileId,
    colorSwatches,
    setColorSwatches,
    studioRecolorOptions,
    setStudioRecolorOptions,
    studioRotationAngle,
    setStudioRotationAngle,
    studioRotationMode,
    setStudioRotationMode,
    studioRotationModel,
    setStudioRotationModel,
    studioRotationQuality,
    setStudioRotationQuality,
    studioAvatar,
    setStudioAvatar,
    studioTechnicalDrawingMode,
    setStudioTechnicalDrawingMode,
    studioTechnicalDrawingMaterialImage,
    setStudioTechnicalDrawingMaterialImage,
    studioTechnicalDrawingMaterial,
    setStudioTechnicalDrawingMaterial,
    studioTechnicalDrawingShadowStyle,
    setStudioTechnicalDrawingShadowStyle,
    studioDetailsFocus,
    setStudioDetailsFocus,
    studioDetailsMode,
    setStudioDetailsMode,
    studioDetailsCameraPerspective,
    setStudioDetailsCameraPerspective,
    studioDetailsMaterial,
    setStudioDetailsMaterial,
    studioDetailsMaterialReference,
    setStudioDetailsMaterialReference,
    studioCutoutShadows,
    setStudioCutoutShadows,
    studioBackgroundRemoval,
    setStudioBackgroundRemoval,
    studioProductDetails,
    setStudioProductDetails,
    handleDownloadImage,
    setActiveViewer,
    projectId,
    isLoadingProducts,
    handleGenerateForSinglePlant,
  } = useProjectContext();

  const navigate = useNavigate();

  // Set project context for backend AI generation (GCS path construction)
  React.useEffect(() => {
    if (projectId) setGenerationContext(projectId);
  }, [projectId]);

  const { data: workflowsData } = useListUserWorkflows();

  const workflows = React.useMemo(() => {
    if (!workflowsData?.workflows) return [];
    return workflowsData.workflows.map((workflow) => {
      const settings = (workflow.settings || {}) as {
        nodes?: WorkflowNode[];
        edges?: WorkflowEdge[];
        createdAt?: number;
      };
      return {
        id: workflow.id,
        name: workflow.name,
        nodes: settings.nodes || [],
        edges: settings.edges || [],
        createdAt: settings.createdAt || Date.now(),
        lastEditedAt: workflow.lastEditedAt ?? undefined,
        lastUsedAt: workflow.lastUsedAt ?? undefined,
      };
    });
  }, [workflowsData]);

  const [studioViewMode, setStudioViewMode] = React.useState<
    "all" | "scenes" | "products"
  >("all");
  const [configuringSceneId, setConfiguringSceneId] = React.useState<
    string | null
  >(null);
  // In-flight scene-row creations, keyed by scene id. The config modal opens
  // before the row exists, so config writes await the entry for their scene.
  const pendingSceneCreationsRef = React.useRef(
    new Map<string, Promise<void>>()
  );
  const [isQuickSelectOpen, setIsQuickSelectOpen] = React.useState(false);
  const [quickSelectInput, setQuickSelectInput] = React.useState("");
  const [isSelectionMenuOpen, setIsSelectionMenuOpen] = React.useState(false);
  const [linkingState, setLinkingState] = React.useState<{
    active: boolean;
    sourceProductId: string | null;
    selectedIds: Set<string>;
  }>({ active: false, sourceProductId: null, selectedIds: new Set() });

  const selectionMenuRef = React.useRef<HTMLDivElement>(null);
  const emptyStateUploadRef = React.useRef<HTMLInputElement>(null);

  // Excel dimension import (SIS-533): preview/apply dialog state, plus a
  // mirror ref so async handlers (mixed image+Excel drops) match against the
  // products state *after* the awaited uploads landed, not a stale closure.
  const [measurementImport, setMeasurementImport] = React.useState<{
    fileName: string;
    rows: ExcelRow[];
    sheetName: string;
    sheets: { sheet: string; data: ExcelRow[] }[];
  } | null>(null);
  const measurementImportRef = React.useRef(measurementImport);
  measurementImportRef.current = measurementImport;
  const saveMeasurements = useProductMeasurements(
    studioProducts,
    setStudioProducts
  );
  const [excelImport, setExcelImport] =
    React.useState<ExcelImportErrorState | null>(null);
  // Dialog-state mirror for handlers that run outside the render cycle (the
  // background flush below, drops landing while a dialog is open): they must
  // see the phase at CALL time, not at closure time.
  const excelImportRef = React.useRef(excelImport);
  React.useEffect(() => {
    excelImportRef.current = excelImport;
  }, [excelImport]);
  const studioProductsRef = React.useRef(studioProducts);
  React.useEffect(() => {
    studioProductsRef.current = studioProducts;
  }, [studioProducts]);
  // A spreadsheet from a mixed drop parks here while the oversized-image
  // warning queue is open; parsing immediately would stack two modals and
  // match against products that don't exist yet.
  const pendingSpreadsheetRef = React.useRef<File | null>(null);

  const {
    aiWatermarkEnabled,
    setAiWatermarkEnabled,
    isLoading: exportSettingsLoading,
  } = useUserSettings();

  // Snapshot the selected items when opening a dialog so selection changes
  // cannot silently retarget a batch that is being reviewed or sent.
  const [exportDialog, setExportDialog] = React.useState<{
    kind: "shopvibes" | "links";
    items: ShopvibesPushItem[];
  } | null>(null);

  const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
  const [showFileSizeWarning, setShowFileSizeWarning] = React.useState(false);
  const [pendingFile, setPendingFile] = React.useState<File | null>(null);
  const pendingOversizedFilesRef = React.useRef<File[]>([]);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [uploadingFiles, setUploadingFiles] = React.useState<
    { id: string; name: string; previewUrl: string }[]
  >([]);
  const uploadingFilesRef = React.useRef(uploadingFiles);
  React.useEffect(() => {
    uploadingFilesRef.current = uploadingFiles;
  }, [uploadingFiles]);
  const {
    activeFlow: quickStartFlow,
    start: startQuickStart,
    complete: completeQuickStart,
    skip: skipQuickStart,
  } = useQuickStartTour();
  // This page only runs the images tour; the tabs own the other flows.
  const isQuickStartActive = quickStartFlow === "images";
  // Which product the tour is currently focused on. Set when the user
  // kicks off generation; lives in component state because it's only
  // meaningful while the user is on this page.
  const [quickStartProductId, setQuickStartProductId] = React.useState<
    string | null
  >(null);
  // The "tour complete" celebration overlay is purely UI state — it
  // shows after the user clicks the result image at the view_result
  // stage and is dismissed by the celebration's action button.
  const [isQuickStartCelebrating, setIsQuickStartCelebrating] =
    React.useState(false);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectionMenuRef.current &&
        !selectionMenuRef.current.contains(event.target as Node)
      ) {
        setIsSelectionMenuOpen(false);
        setIsQuickSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // The visible quick-start sub-stage is derived from observable page
  // state at render time, not stored. This keeps the DB to a single
  // 3-state status while still letting the UI show the right prompt.
  const quickStartStage: QuickstartStage | null = React.useMemo(
    () =>
      deriveQuickStartStage({
        isActive: isQuickStartActive,
        products: studioProducts,
        hasScene: Boolean(studioScene),
        hasVibe: Boolean(studioVibe),
        imageType: studioImageType,
        detailsMode: studioDetailsMode,
        technicalDrawingMode: studioTechnicalDrawingMode,
      }),
    [
      isQuickStartActive,
      studioProducts,
      studioScene,
      studioVibe,
      studioImageType,
      studioDetailsMode,
      studioTechnicalDrawingMode,
    ]
  );

  // The catalog entry behind the current image type, or undefined when a
  // workflow is selected. Never fall back to the first entry: the empty
  // state would then claim "Milieu Creative" while the upload runs the
  // workflow (StudioPanel guards the same case).
  const currentGoal = findCreationEntry(studioImageType);
  const quickStartMode = currentGoal ? currentGoal.id : "workflow";

  // Fire `quickstart_stage_reached` once per stage per page mount. `upload`
  // precedes the goal choice (the empty state's cards, SIS-565), so it
  // carries no mode and fires once; the later stages are deduped per stage
  // and mode, so switching goals after the upload counts as entering the
  // stage again for the new goal. Cross-session reload re-emits, which is
  // acceptable noise for a tour that only runs once per user.
  const seenStagesRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    if (!quickStartStage) return;
    const mode = quickStartStage === "upload" ? undefined : quickStartMode;
    const key = mode ? `${quickStartStage}:${mode}` : quickStartStage;
    if (seenStagesRef.current.has(key)) return;
    seenStagesRef.current.add(key);
    trackQuickstartStageReached(quickStartStage, "images", mode);
  }, [quickStartStage, quickStartMode]);

  const skipQuickStartFromCurrent = React.useCallback(() => {
    trackQuickstartSkipped(quickStartStage, "images");
    skipQuickStart();
    setQuickStartProductId(null);
    setIsQuickStartCelebrating(false);
  }, [skipQuickStart, quickStartStage]);

  const finalizeQuickStart = React.useCallback(() => {
    trackQuickstartCompleted("images");
    completeQuickStart();
    setQuickStartProductId(null);
    setIsQuickStartCelebrating(false);
  }, [completeQuickStart]);

  const startQuickStartGeneration = React.useCallback(
    (productId: string) => {
      if (quickStartStage !== "generate") return;
      setQuickStartProductId(productId);
    },
    [quickStartStage]
  );

  const completeQuickStartGeneration = React.useCallback(
    (productId: string, ok: boolean) => {
      if (quickStartProductId !== productId) return;
      if (!ok) setQuickStartProductId(null);
      // Success path: derived stage flips to view_result on its own once
      // the new generatedContent is observable, so no state write here.
    },
    [quickStartProductId]
  );

  // --- Handlers ---

  const runProductAnalysis = useProductAnalysis(setStudioProducts);

  const processSingleFile = async (file: File): Promise<StudioProduct> => {
    const product = await uploadFileAsProduct(
      file,
      projectId,
      auth.currentUser!.uid
    );
    // Pessimistic persist (SIS-451): the product may only enter local state
    // once its DB row exists, otherwise later image generations hit FK
    // violations on studio_image.product_id. Failures propagate to the
    // caller, which reports and toasts them exactly once.
    await persistStudioProduct(projectId, product);
    void runProductAnalysis(product.id, product.originalImage);
    return product;
  };

  const handleRetryProductAnalysis = React.useCallback(
    (productId: string) => {
      const product = studioProducts.find((p) => p.id === productId);
      if (!product) return;
      void runProductAnalysis(product.id, product.originalImage);
    },
    [runProductAnalysis, studioProducts]
  );

  const processNextOversizedFile = async () => {
    const fileToProcess = pendingFile;
    // Dismiss modal immediately so the UI feels responsive
    setPendingFile(null);
    setShowFileSizeWarning(false);

    if (fileToProcess) {
      try {
        const product = await processSingleFile(fileToProcess);
        // Ref append BEFORE setState (updaters run at render time, not call
        // time) — flushPendingSpreadsheet() below must see this product.
        studioProductsRef.current = [...studioProductsRef.current, product];
        setStudioProducts((prev) => [...prev, product]);
      } catch (err) {
        reportError(err, {
          context: "product_upload",
          stage: "oversized_retry",
          projectId,
          fileName: sanitizeFileNameForReport(fileToProcess.name),
          fileSize: fileToProcess.size,
          fileType: fileToProcess.type,
        });
        toast.error(resolveErrorMessage(err, t));
      }
    }
    const next = pendingOversizedFilesRef.current.shift();
    if (next) {
      setPendingFile(next);
      setShowFileSizeWarning(true);
    } else {
      flushPendingSpreadsheet();
    }
  };

  /** Resumes a spreadsheet parked behind the oversized-image queue. */
  const flushPendingSpreadsheet = () => {
    const sheet = pendingSpreadsheetRef.current;
    pendingSpreadsheetRef.current = null;
    if (!sheet) return;
    // A dialog the user opened in the meantime (or a running apply) must not
    // be torn down by a background upload finishing. Discard the parked sheet
    // audibly instead — the file is still on disk and re-importing is two
    // clicks; silently replacing a mid-persist dialog is not recoverable.
    if (excelImportRef.current || measurementImportRef.current) {
      toast.info(t("studio.excelImport.parkedDiscarded", { name: sheet.name }));
      return;
    }
    // handleExcelFile is total by design; the catch mirrors the other entry
    // points as defense-in-depth.
    handleExcelFile(sheet).catch((err) => {
      reportError(err, {
        context: "excel_dimension_import",
        stage: "flush",
        projectId,
        fileName: sanitizeFileNameForReport(sheet.name),
      });
      toast.error(resolveErrorMessage(err, t));
    });
  };

  const processFiles = async (files: File[]) => {
    // Magic bytes decide, not `file.type`: the input's `accept` list is only a
    // picker hint, drag-and-drop ignores it, and browsers report an empty type
    // for unknown extensions — which this filter used to wave through, letting
    // e.g. a .fbx 3D model become a product photo (IMAGE-STUDIO-4Z).
    const sniffed = await Promise.all(files.map((f) => detectImageMimeType(f)));
    const imageFiles = files.filter((_, i) => sniffed[i] !== null);
    const rejectedNames = files
      .filter((_, i) => sniffed[i] === null)
      .map((f) => f.name);
    if (rejectedNames.length > 0) {
      // Previously dropped silently — the user was left with no feedback.
      toast.error(
        t("studio.uploadUnsupportedFiles", {
          names: formatRejectedFileNames(rejectedNames),
        })
      );
    }
    const oversizedFiles: File[] = [];
    const normalFiles: File[] = [];
    for (const file of imageFiles) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        oversizedFiles.push(file);
      } else {
        normalFiles.push(file);
      }
    }

    // Create uploading placeholders with preview thumbnails
    const uploadEntries = normalFiles.map((file) => ({
      id: crypto.randomUUID(),
      name: file.name,
      previewUrl: URL.createObjectURL(file),
    }));
    if (uploadEntries.length > 0) {
      setUploadingFiles((prev) => [...prev, ...uploadEntries]);
    }

    const newProducts: StudioProduct[] = [];
    for (let i = 0; i < normalFiles.length; i++) {
      const entry = uploadEntries[i];
      const file = normalFiles[i];
      try {
        newProducts.push(await processSingleFile(file));
      } catch (err) {
        reportError(err, {
          context: "product_upload",
          stage: "process_files",
          projectId,
          fileName: sanitizeFileNameForReport(file.name),
          fileSize: file.size,
          fileType: file.type,
        });
        toast.error(resolveErrorMessage(err, t));
      }
      // Remove this entry from uploading state
      setUploadingFiles((prev) => {
        const next = prev.filter((e) => e.id !== entry.id);
        if (next.length === prev.length) return prev;
        return next;
      });
      URL.revokeObjectURL(entry.previewUrl);
    }
    if (newProducts.length > 0) {
      // Ref append BEFORE setState: React runs functional updaters at render
      // time, not at call time, so a spreadsheet from the same drop that
      // parses right after this call would otherwise race the commit. The
      // passive ref effect reconciles the mirror to state afterwards.
      studioProductsRef.current = [
        ...studioProductsRef.current,
        ...newProducts,
      ];
      setStudioProducts((prev) => [...prev, ...newProducts]);
      for (const p of newProducts) {
        trackImageUploaded(projectId, p.id);
      }
    }
    if (oversizedFiles.length > 0) {
      pendingOversizedFilesRef.current = oversizedFiles.slice(1);
      setPendingFile(oversizedFiles[0]);
      setShowFileSizeWarning(true);
    }
    // Callers with follow-up work (a dropped spreadsheet) must defer it until
    // the size-warning queue has been answered.
    return { queuedOversized: oversizedFiles.length > 0 };
  };

  const handleStudioImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;
    if (!files) return;
    try {
      await processFiles(Array.from(files));
    } finally {
      // Reset even on failure, or re-picking the same file is a no-op (the
      // input's value would still match and no change event would fire).
      e.target.value = "";
    }
  };

  /**
   * Parses a spreadsheet and opens the import dialog in the matching phase.
   * Never throws — every failure path lands in the dialog's error phase, where
   * the template/help links are, instead of a dead-end toast.
   */
  const handleExcelFile = async (file: File) => {
    // Do not replace an active mapping/import session with another workbook.
    if (measurementImportRef.current) {
      toast.info(t("studio.excelImport.importInProgress"));
      return;
    }
    if (!XLSX_FILENAME_RE.test(file.name)) {
      setExcelImport({
        fileName: file.name,
        error: "unsupportedFormat",
      });
      return;
    }
    if (file.size > MAX_SHEET_FILE_BYTES) {
      setExcelImport({
        fileName: file.name,
        error: "tooLarge",
      });
      return;
    }
    let rows;
    let sheets;
    try {
      // Lazy import: keeps the xlsx parser (plus fflate/saxen) out of the
      // studio route chunk — almost no session ever imports a sheet.
      const { default: readWorkbook } = await import("read-excel-file/browser");
      sheets = (await readWorkbook(file)).filter((s) => s.data.length);
      rows = sheets[0]?.data ?? [];
    } catch (err) {
      reportError(err, {
        context: "excel_dimension_import",
        stage: "read",
        projectId,
        fileName: sanitizeFileNameForReport(file.name),
      });
      setExcelImport({
        fileName: file.name,
        error: "unreadable",
      });
      return;
    }
    if (!rows.length) {
      setExcelImport({
        fileName: file.name,
        error: "emptySheet",
      });
      return;
    }
    setExcelImport(null);
    setMeasurementImport({
      fileName: file.name,
      rows,
      sheetName: sheets[0].sheet,
      sheets,
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
    if (files.length === 0) return;
    // Spreadsheets branch off before the image pipeline, whose magic-byte
    // filter would otherwise reject them with the generic unsupported-file
    // toast. On a mixed drop the images upload first, so the Excel matching
    // runs against the freshly added products.
    const spreadsheets = files.filter((f) =>
      SPREADSHEET_FILENAME_RE.test(f.name)
    );
    const imageFiles = files.filter(
      (f) => !SPREADSHEET_FILENAME_RE.test(f.name)
    );
    // Fire-and-forget: per-file failures are already handled inside, so a
    // rejection here means something unexpected — surface it rather than
    // letting it land as an unhandled rejection.
    void (async () => {
      let queuedOversized = false;
      if (imageFiles.length > 0) {
        ({ queuedOversized } = await processFiles(imageFiles));
      }
      if (spreadsheets.length > 0) {
        if (queuedOversized) {
          if (pendingSpreadsheetRef.current) {
            // A sheet from an earlier drop is already parked — keep it.
            // Silently replacing an import the user already initiated would
            // lose it without a trace (the flush only ever sees one file).
            toast.info(t("studio.excelImport.onlyOneFile"));
          } else {
            // The size-warning modal is open and its products don't exist
            // yet — the sheet resumes when that queue drains (accepted or
            // canceled).
            pendingSpreadsheetRef.current = spreadsheets[0];
          }
        } else {
          await handleExcelFile(spreadsheets[0]);
        }
        if (spreadsheets.length > 1) {
          toast.info(t("studio.excelImport.onlyOneFile"));
        }
      }
    })().catch((err) => {
      reportError(err, { context: "product_upload", stage: "drop", projectId });
      toast.error(resolveErrorMessage(err, t));
    });
  };

  const handleMenuImagesSelected = (files: File[]) => {
    void processFiles(files).catch((err) => {
      reportError(err, {
        context: "product_upload",
        stage: "picker",
        projectId,
      });
      toast.error(resolveErrorMessage(err, t));
    });
  };

  const handleMenuExcelSelected = (file: File) => {
    void handleExcelFile(file).catch((err) => {
      reportError(err, {
        context: "excel_dimension_import",
        stage: "picker",
        projectId,
      });
      toast.error(resolveErrorMessage(err, t));
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleAddSwatch = (swatch: any) =>
    setColorSwatches((prev) => [...prev, swatch]);
  const handleRemoveSwatch = (id: string) =>
    setColorSwatches((prev) => prev.filter((s) => s.id !== id));
  const handleToggleSwatch = (id: string) =>
    setColorSwatches((prev) =>
      prev.map((s) => (s.id === id ? { ...s, isSelected: !s.isSelected } : s))
    );
  const handleUpdateSwatch = (id: string, updates: Partial<ColorSwatch>) =>
    setColorSwatches((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...updates } : s))
    );

  const handleQuickStartVibeChange = (vibeId: string) => {
    setStudioVibe(vibeId);
  };

  const handleQuickStartSceneChange = (sceneId: string) => {
    setStudioScene(sceneId);
  };

  // Product Detail handlers
  const handleAddProductDetail = (detail: ProductDetail) =>
    setStudioProductDetails((prev) => [...prev, detail]);
  const handleRemoveProductDetail = (id: string) =>
    setStudioProductDetails((prev) => prev.filter((d) => d.id !== id));
  const handleUpdateProductDetail = (
    id: string,
    updates: Partial<ProductDetail>
  ) =>
    setStudioProductDetails((prev) =>
      prev.map((d) => (d.id === id ? { ...d, ...updates } : d))
    );

  const handleStartLinking = (id: string) => {
    setLinkingState({
      active: true,
      sourceProductId: id,
      selectedIds: new Set([id]),
    });
  };

  const handleToggleLinkSelection = (id: string) => {
    if (!canSelectMultiProductSceneProduct(linkingState.selectedIds, id)) {
      toast.error(
        t("studio.multiProductMaxReached", {
          max: MAX_MULTI_PRODUCT_SCENE_PRODUCTS,
        })
      );
      return;
    }

    setLinkingState((prev) => {
      const next = new Set(prev.selectedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, selectedIds: next };
    });
  };

  const handleCancelLinking = () => {
    setLinkingState({
      active: false,
      sourceProductId: null,
      selectedIds: new Set(),
    });
  };

  const handleSaveLinks = async () => {
    if (!linkingState.sourceProductId || linkingState.selectedIds.size < 2)
      return;
    if (exceedsMultiProductSceneLimit(linkingState.selectedIds.size)) {
      toast.error(
        t("studio.multiProductMaxReached", {
          max: MAX_MULTI_PRODUCT_SCENE_PRODUCTS,
        })
      );
      return;
    }
    const allSelectedIds = Array.from(linkingState.selectedIds);

    const newScene: MultiProductScene = {
      id: crypto.randomUUID(),
      name: `Scene ${multiProductScenes.length + 1}`,
      // No role by default. Assigning "center" to whichever product happened to
      // be selected first made the app, not the user, decide the scene's focus
      // — and the prompt then staged the composition around it (SIS-537).
      products: allSelectedIds.map((productId) => ({
        productId: productId as string,
        role: "organic" as ProductRole,
        quantity: 1,
      })),
      generatedContent: [],
      isGenerating: false,
      error: null,
      createdAt: Date.now(),
      quality: "1K", // Default quality
    };

    setMultiProductScenes((prev) => [...prev, newScene]);
    setConfiguringSceneId(newScene.id);
    setLinkingState({
      active: false,
      sourceProductId: null,
      selectedIds: new Set(),
    });

    // Persist the new scene to the database. The config modal is already open
    // at this point, so the creation is tracked and config writes queue behind
    // it — an update that lands first would match no row and be discarded.
    const creation = persistMultiProductScene(projectId, newScene);
    pendingSceneCreationsRef.current.set(newScene.id, creation);
    try {
      await creation;
    } catch (err) {
      console.error("Failed to persist multiproduct scene", err);
      setError("Failed to save multiproduct scene to cloud.");
    } finally {
      pendingSceneCreationsRef.current.delete(newScene.id);
    }
  };

  const handleDeleteScene = async (sceneId: string) => {
    setMultiProductScenes((prev) => prev.filter((s) => s.id !== sceneId));

    // Delete from database
    try {
      await deleteMultiProductScene(projectId, sceneId);
    } catch (err) {
      console.error("Failed to delete multiproduct scene", err);
      setError("Failed to delete multiproduct scene from cloud.");
    }
  };

  const handleUpdateSceneConfig = async (sceneId: string, updates: any) => {
    const baseScene = multiProductScenes.find((s) => s.id === sceneId);
    // Stamp the display labels whenever an environment id changes: a brand
    // save recreates every vibe/scene row with new ids (SIS-474), and the
    // label is what lets the saved selection be found again afterwards.
    const withLabels = { ...updates };
    if ("vibeId" in updates) {
      withLabels.vibeLabel =
        vibes.find((v) => v.id === updates.vibeId)?.label ?? "";
    }
    if ("sceneId" in updates) {
      withLabels.sceneLabel =
        scenes.find((s) => s.id === updates.sceneId)?.label ?? "";
    }
    setMultiProductScenes((prev) =>
      prev.map((s) =>
        s.id === sceneId
          ? {
              ...s,
              ...withLabels,
            }
          : s
      )
    );

    // Persist the modal config (vibe/scene/prompt/override/AR/quality) so it
    // survives a reload (SIS-501). Built from the same merge the state update
    // above applies, since setState hasn't flushed yet.
    if (baseScene) {
      try {
        // A scene created moments ago may still be inserting — updateMany
        // against a missing row succeeds with zero matches and would drop the
        // user's config silently.
        await pendingSceneCreationsRef.current.get(sceneId);
        await updateMultiProductSceneConfig({ ...baseScene, ...withLabels });
      } catch (err) {
        reportError(err, {
          context: "multiProductSceneConfigPersist",
          sceneId,
        });
        setError(resolveErrorMessage(err, t));
      }
    }

    // If products were updated, persist the new links
    if (updates.products) {
      try {
        await updateMultiProductSceneLinks(sceneId, updates.products);
      } catch (err) {
        console.error("Failed to update multiproduct scene links", err);
        setError("Failed to update multiproduct scene configuration.");
      }
    }
  };

  const handleStudioProductDelete = async (id: string) => {
    // Find product before removing from state
    const productToDelete = studioProducts.find((p) => p.id === id);

    // Optimistic UI update
    setStudioProducts((prev) => prev.filter((p) => p.id !== id));
    trackProductDeleted(projectId, id);

    if (productToDelete && projectId) {
      try {
        await deleteStudioProduct(projectId, productToDelete);
      } catch (e) {
        console.error("Failed to delete product", e);
        setError(
          "Failed to delete product from server. It may reappear on reload."
        );
        // Revert UI if needed? For now, we just warn.
      }
    }
  };
  const handlePlantSelectionChange = (id: string, isSelected: boolean) =>
    setStudioProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, isSelected } : p))
    );
  const handlePlantDimensionsChange = (
    id: string,
    updates: { width?: string; height?: string }
  ) => {
    setStudioProducts((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              width: updates.width,
              height: updates.height,
            }
          : p
      )
    );
  };
  const handleProductSaveStateChange = (
    productId: string,
    state: { isSaving?: boolean; lastSaved?: number; saveError?: string | null }
  ) => {
    setStudioProducts((prev) =>
      prev.map((p) => (p.id === productId ? { ...p, ...state } : p))
    );
  };
  const handlePlantInfoChange = (id: string, info: string) => {
    const match = info.match(
      /(?:^|\s)(\d+)\s*(?:pcs|pc|x|stk|st|items|pieces|stems|roses)/i
    );
    let newCount: number | null = null;
    if (match && match[1]) {
      const count = parseInt(match[1], 10);
      if (!isNaN(count) && count > 0) newCount = count;
    }
    setStudioProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, info, itemCount: newCount } : p))
    );
  };

  const handleRetryBackgroundRemoval = async (id: string) => {
    const product = studioProducts.find((p) => p.id === id);
    if (!product || product.isProcessing) return;

    setStudioProducts((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, isProcessing: true, error: null } : p
      )
    );

    // Named before the call — the settings handed to the backend are what it
    // writes the DB row from, so a title set on the result afterwards never
    // reaches it (SIS-517). The template also replaces the provider's own
    // object name, which is what this used to file the result under.
    const filename = generateUniqueFilename(
      namingTemplate,
      {
        originalName: product.originalImage.name,
        type: "Background_Removal",
      },
      product.generatedContent.map((c) => c.title)
    );

    try {
      const noBgResult = await executeBackgroundRemoval(
        product.originalImage,
        (message) => {
          // Update product with progress message
          setStudioProducts((prev) =>
            prev.map((p) =>
              p.id === id
                ? { ...p, error: message } // Temporarily use error field for progress
                : p
            )
          );
        },
        projectId,
        id,
        "Background Removal",
        serializeGenerationSettings({
          imageType: "Background Removal",
          title: filename,
        })
      );

      const result: GeneratedContent = {
        id: noBgResult.dbId ?? crypto.randomUUID(),
        image: noBgResult.downloadUrl,
        storagePath: noBgResult.storagePath,
        thumbnailUrl: noBgResult.downloadUrl,
        text: "Background Removed",
        title: filename,
        video: null,
        tags: ["No Background", "Transparent"],
        imageType: "Background Removal",
        generationSettings: {
          imageType: "Background Removal",
          title: filename,
        },
        timestamp: Date.now(),
        filename,
        mimeType: noBgResult.mimeType,
        dbPersisted: !!noBgResult.dbId,
      };

      // Add to generatedContent (keep product.image unchanged) — must happen
      // before persist so the .then() can find the content by its old ID.
      setStudioProducts((prev) =>
        prev.map((p) =>
          p.id === id
            ? {
                ...p,
                generatedContent: [result, ...p.generatedContent],
                isProcessing: false,
                error: null,
              }
            : p
        )
      );

      if (!result.dbPersisted) {
        // Persist the generated content
        const bgOldId = result.id;
        persistGeneratedContent(projectId, id, result)
          .then((dbId) => {
            setStudioProducts((prev) =>
              prev.map((p) => {
                if (p.id !== id) return p;
                return {
                  ...p,
                  generatedContent: p.generatedContent.map((c) =>
                    c.id === bgOldId ? { ...c, id: dbId } : c
                  ),
                };
              })
            );
            setActiveViewer((prev) =>
              prev && prev.content.id === bgOldId
                ? {
                    ...prev,
                    content: { ...prev.content, id: dbId },
                    currentContentId:
                      prev.currentContentId === bgOldId
                        ? dbId
                        : prev.currentContentId,
                  }
                : prev
            );
          })
          .catch((e) =>
            console.error("Failed to persist BG removal content", e)
          );
      }
    } catch (err) {
      console.error("BG Removal failed", err);
      const errorMessage = resolveErrorMessage(err, t);
      setStudioProducts((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, isProcessing: false, error: errorMessage } : p
        )
      );
      // Credit deduction is handled by service, no refund needed as it only deducts on success
    }
  };

  const handleViewSourceImage = (productId: string, initialTab?: "details") => {
    const product = studioProducts.find((p) => p.id === productId);
    if (product) {
      if (
        quickStartStage === "view_result" &&
        (!quickStartProductId || quickStartProductId === productId) &&
        product.generatedContent.length > 0
      ) {
        setIsQuickStartCelebrating(true);
      }
      const src = product.originalImage;
      const content: GeneratedContent = {
        id: "source",
        title: src.name,
        image: getImageSrc(src) || null,
        storagePath: src.storagePath,
        thumbnailUrl: src.thumbnailUrl,
        text: "Source Image",
        video: null,
        timestamp: src.uploadedAt || undefined,
      };
      setActiveViewer({
        content,
        productId,
        currentContentId: null,
        initialTab,
      });
    }
  };

  const handlePlantViewImage = (productId: string, contentId: string) => {
    const product = studioProducts.find((p) => p.id === productId);
    const content = product?.generatedContent.find((c) => c.id === contentId);
    if (content && product) {
      if (
        quickStartStage === "view_result" &&
        (!quickStartProductId || quickStartProductId === productId)
      ) {
        setIsQuickStartCelebrating(true);
      }
      setActiveViewer({ content, productId, currentContentId: contentId });
    }
  };

  const handleViewSceneImage = (sceneId: string, contentId: string) => {
    const scene = multiProductScenes.find((s) => s.id === sceneId);
    if (!scene) return;

    const content = scene.generatedContent.find((c) => c.id === contentId);
    if (!content) return;

    setActiveViewer({
      content,
      productId: sceneId,
      currentContentId: contentId,
    });
  };

  const handleGenerateForScene = async (
    sceneId: string,
    size: "1K" | "2K" | "4K" = "1K",
    overrides?: Partial<
      Pick<
        MultiProductScene,
        | "products"
        | "vibeId"
        | "sceneId"
        | "aspectRatio"
        | "vibeContext"
        | "quality"
        | "overrideContext"
        | "includeSizeComparison"
      >
    >
  ) => {
    const baseScene = multiProductScenes.find((s) => s.id === sceneId);
    const scene = baseScene
      ? {
          ...baseScene,
          ...(overrides || {}),
          // The config modal sends ids only. Its selection is authoritative,
          // so the stored label — which describes the *previous* one — is
          // dropped: otherwise clearing the vibe would resolve straight back
          // to it by label.
          ...(overrides && "vibeId" in overrides
            ? { vibeLabel: undefined }
            : {}),
          ...(overrides && "sceneId" in overrides
            ? { sceneLabel: undefined }
            : {}),
        }
      : undefined;
    if (!scene || scene.products.length === 0) return;

    if (exceedsMultiProductSceneLimit(scene.products.length)) {
      toast.error(
        t("studio.multiProductMaxReached", {
          max: MAX_MULTI_PRODUCT_SCENE_PRODUCTS,
        })
      );
      return;
    }

    const hasPendingAnalysis = scene.products.some((sp) => {
      const product = studioProducts.find((p) => p.id === sp.productId);
      return product?.analysisStatus === "pending";
    });
    if (hasPendingAnalysis) {
      toast.error(t("studio.waitingForAnalysis"));
      return;
    }

    // Mark scene as generating
    setMultiProductScenes((prev) =>
      prev.map((s) =>
        s.id === sceneId ? { ...s, isGenerating: true, error: null } : s
      )
    );

    try {
      // Get settings from scene-specific config or fall back to panel
      // settings. Deliberately NOT for the custom prompt: multi-product
      // generations only respect a prompt entered in the scene config modal —
      // the studio textarea belongs to other modes and is hidden here, and
      // the persisted metadata must never claim a prompt that was not sent
      // (SIS-500).
      // Resolve the saved ids against the brand's current options: a brand
      // save recreates every vibe/scene row (SIS-474), and a stale id is
      // truthy enough to block the panel fallback while resolving to nothing,
      // which would silently generate with default lighting and no scene.
      const sceneVibeId =
        resolveEnvironmentReference(scene.vibeId, scene.vibeLabel, vibes) ||
        studioVibe;
      const sceneSceneId =
        resolveEnvironmentReference(scene.sceneId, scene.sceneLabel, scenes) ||
        studioScene;
      const sceneAspectRatio = scene.aspectRatio || studioAspectRatio;
      const sceneVibeContext = scene.vibeContext || undefined;

      const isTrueToneStudio = sceneVibeId === "true-tone-studio";
      const selectedVibeObj = isTrueToneStudio
        ? null
        : vibes.find((v) => v.id === sceneVibeId);
      const selectedSceneObj = scenes.find((s) => s.id === sceneSceneId);
      const vibeLabel = isTrueToneStudio
        ? "True-Tone Studio"
        : selectedVibeObj?.label || sceneVibeId || "";

      // A custom-prompt override replaces the scene AND silences the vibe's
      // lighting (SIS-537 follow-up): neither influenced the image, so the
      // persisted metadata, tags and filename must not claim them — the same
      // "never record a prompt that was not sent" rule as SIS-500. The typed
      // prompt itself stays recorded as `context`.
      // Trimmed like the backend's own check: a whitespace-only prompt is not
      // an override there, so it must not strip the metadata here either.
      const overridesScene = !!(
        scene.overrideContext && sceneVibeContext?.trim()
      );
      const recordedVibeLabel = overridesScene ? undefined : vibeLabel;
      const recordedVibeId = overridesScene ? undefined : sceneVibeId;
      const recordedSceneLabel = overridesScene
        ? undefined
        : selectedSceneObj?.label || sceneSceneId;
      const recordedSceneId = overridesScene ? undefined : sceneSceneId;

      const selectedProducts = scene.products.map((sp) => ({
        sceneProduct: sp,
        product: studioProducts.find((p) => p.id === sp.productId),
      }));

      const missingProducts = selectedProducts
        .filter(({ product }) => !product)
        .map(({ sceneProduct }) => sceneProduct.productId);
      if (missingProducts.length > 0) {
        throw new AppError(
          "generation/multi_product_products_unavailable",
          `Scene contains products that are no longer available: ${missingProducts.join(", ")}`
        );
      }

      const missingImages = selectedProducts
        .filter(({ product }) => !product?.image?.storagePath)
        .map(
          ({ product, sceneProduct }) =>
            product?.originalImage.name.split(".")[0] || sceneProduct.productId
        );
      if (missingImages.length > 0) {
        throw new AppError(
          "generation/multi_product_missing_images",
          `Scene generation requires every selected product to have an uploaded image. Missing: ${missingImages.join(", ")}`
        );
      }

      // Build product images array — pass storagePaths (backend reads from GCS).
      // Names are derived as a batch so products that share a category get
      // distinct, sibling-aware names. User overrides win and are reserved so
      // auto names never duplicate an explicit one.
      const reservedNames = selectedProducts
        .map(({ sceneProduct }) => sceneProduct.nameOverride?.trim())
        .filter((n): n is string => !!n);
      const autoNames = buildPromptProductNames(
        selectedProducts.map(({ product }) => product!),
        reservedNames
      );
      // Dimensions travel as normalized numeric strings, parsed with the same
      // parser the size-guide measurement uses. Sending the raw field text let
      // free-text entries ("30 cm hoch") pass the guide (parseFloat reads 30)
      // while the backend's numeric gate dropped the prompt suffix — guide and
      // prompt then disagreed about a size with no signal anywhere.
      const promptCm = (value?: string): string | undefined => {
        const parsed = parseProductDimensionCm(value, 0);
        return parsed > 0 ? String(parsed) : undefined;
      };
      const productImages = selectedProducts.map(
        ({ sceneProduct, product }) => {
          const overrideName = sceneProduct.nameOverride?.trim();
          return {
            name:
              overrideName ||
              autoNames.get(product!.id) ||
              buildPromptProductName(product!),
            role: sceneProduct.role,
            quantity: sceneProduct.quantity,
            data: "",
            mimeType: product!.image.mimeType || "image/png",
            storagePath: product!.image.storagePath!,
            widthCm: promptCm(product!.width),
            heightCm: promptCm(product!.height),
          };
        }
      );

      if (productImages.length < 2) {
        throw new AppError(
          "generation/multi_product_min_products",
          "Multi-product generation requires at least two products."
        );
      }

      // Every product must carry a name the model can act on. The prompt says
      // `Input Image N is a "<name>"`, so an empty one would either break the
      // sentence or — as before SIS-537, when the file name was the fallback —
      // declare a storage id as the product's type. The config modal blocks
      // this; the guard covers any other path in.
      const unnamedProducts = productImages
        .map((image, i) => ({ image, index: i }))
        .filter(({ image }) => !image.name.trim())
        .map(({ index }) => selectedProducts[index].sceneProduct.productId);
      if (unnamedProducts.length > 0) {
        throw new AppError(
          "generation/multi_product_missing_names",
          `Scene generation requires a product type for every product. Missing: ${unnamedProducts.join(", ")}`
        );
      }

      // Use scene-specific quality or fall back to size parameter
      const sceneQuality = scene.quality || size;

      // Create scene config
      const sceneConfig: geminiService.SceneConfig = {
        vibeId: isTrueToneStudio ? "true-tone-studio" : sceneVibeId || "",
        sceneId: sceneSceneId || "",
        quality: sceneQuality,
        aspectRatio: sceneAspectRatio,
        vibeContext: sceneVibeContext,
        overrideContext: scene.overrideContext || false, // Use scene's overrideContext
      };

      let sizeComparisonDataUrl: string | undefined;
      let sizeComparisonDebug: SizeComparisonCanvasDebug | undefined;
      if (scene.includeSizeComparison) {
        // "Checked means delivered": when true-to-scale is on and the guide
        // cannot be built (a dimension edited to garbage after configuring,
        // a corrupt image), generation STOPS before any credit is spent —
        // never a warning toast plus a paid, unscaled image. The modal's own
        // gate catches this earlier for the modal path; this is the
        // enforcement for every other caller (the scene card's Generate
        // button, future workflow paths).
        try {
          const sizeComparison = await createSizeComparisonCanvas(
            selectedProducts.map(({ product }) => ({
              id: product!.id,
              image: product!.image,
              heightInput: product!.height,
              widthInput: product!.width,
              label: product!.originalImage.name.split(".")[0],
            }))
          );
          sceneConfig.sizeComparisonReference = {
            imageDataUrl: sizeComparison.dataUrl,
          };
          sizeComparisonDataUrl = sizeComparison.dataUrl;
          sizeComparisonDebug = sizeComparison.debug;
        } catch (sizeErr) {
          reportError(sizeErr, {
            context: "multiProductSizeComparisonCanvas",
            sceneId,
          });
          throw new AppError(
            "generation/size_guide_failed",
            sizeErr instanceof Error ? sizeErr.message : String(sizeErr)
          );
        }
      }

      // Dev/staging-only: download a local HTML report (never in production).
      if (DEBUG_TOOLS_ENABLED && scene.debugPayloadDownload) {
        await downloadMultiProductDebugReport({
          scene,
          sceneId,
          sceneConfig,
          productImages,
          selectedProducts,
          sizeComparisonDataUrl,
          sizeComparisonDebug,
          vibes,
          scenes,
        });
      }

      // Named before the call — the backend writes the DB row from this
      // context, so a title added to the result afterwards never reaches it
      // and the scene reloads as "Generated Image" (SIS-517).
      const sceneTitle = generateUniqueFilename(
        namingTemplate,
        {
          originalName: scene.name,
          type: "Multi-Product Scene",
          scene: overridesScene ? undefined : selectedSceneObj?.label,
          vibe: recordedVibeLabel,
        },
        scene.generatedContent.map((c) => c.title)
      );

      // Generate scene
      setGenerationContext(
        projectId,
        sceneId,
        "Multi-Product",
        serializeGenerationSettings({
          imageGeneration: { ...studioImageGeneration, resolution: size },
          imageType: "Multi-Product",
          title: sceneTitle,
          vibe: recordedVibeLabel,
          scene: recordedSceneLabel,
          // Persist the resolved ids next to the labels, like every other
          // generation path, so the details panel and any future regeneration
          // read the same environment the scene was generated with (SIS-502).
          ...environmentIdSettings({
            vibe: recordedVibeId,
            scene: recordedSceneId,
          }),
          aspectRatio: sceneAspectRatio,
          resolution: size,
          model: modelType === "standard" ? "Nano Banana" : "Nano Banana Pro",
          // User-typed custom prompt for the scene — must be in the backend
          // payload so it's persisted to DB and shown in image details after
          // the project is reloaded.
          context: sceneVibeContext,
        })
      );
      const result = await geminiService.generateMultiProductScene(
        productImages,
        sceneConfig,
        vibes,
        scenes
      );

      if (!result.image) {
        throw new AppError(
          "generation/no_image_returned",
          "Scene generation failed - no image returned"
        );
      }

      // Create generated content
      const generatedContent: GeneratedContent = {
        ...result,
        id: result.dbPersisted
          ? (result.id ?? crypto.randomUUID())
          : crypto.randomUUID(),
        title: sceneTitle,
        tags: [recordedVibeLabel || "", recordedSceneLabel || ""],
        generationSettings: mergeGeneratedImageSettings(
          result.generationSettings,
          {
            imageType: "Multi-Product",
            title: sceneTitle,
            vibe: recordedVibeLabel,
            scene: recordedSceneLabel,
            ...environmentIdSettings({
              vibe: recordedVibeId,
              scene: recordedSceneId,
            }),
            aspectRatio: sceneAspectRatio,
            resolution: size,
            model: modelType === "standard" ? "Nano Banana" : "Nano Banana Pro",
            context: sceneVibeContext,
          }
        ),
        timestamp: Date.now(),
      };

      // Update scene with generated content
      setMultiProductScenes((prev) =>
        prev.map((s) =>
          s.id === sceneId
            ? {
                ...s,
                isGenerating: false,
                generatedContent: [generatedContent, ...s.generatedContent],
              }
            : s
        )
      );

      // Persist generated content to database (skip if backend already wrote the record)
      if (!generatedContent.dbPersisted) {
        try {
          const sceneOldId = generatedContent.id;
          const dbId = await persistGeneratedContent(
            projectId,
            sceneId,
            generatedContent
          );
          setMultiProductScenes((prev) =>
            prev.map((s) => {
              if (s.id !== sceneId) return s;
              return {
                ...s,
                generatedContent: s.generatedContent.map((c) =>
                  c.id === sceneOldId ? { ...c, id: dbId } : c
                ),
              };
            })
          );
        } catch (err) {
          console.error("Failed to persist scene generated content", err);
        }
      }
    } catch (error: any) {
      console.error("Scene generation failed:", error);
      reportError(error, {
        context: "multiProductSceneGeneration",
        sceneId,
      });
      const message = resolveErrorMessage(error, t);
      setMultiProductScenes((prev) =>
        prev.map((s) =>
          s.id === sceneId
            ? {
                ...s,
                isGenerating: false,
                error: message,
              }
            : s
        )
      );
      // One floating surface only: the toast (plus the inline error on the
      // scene card). Also pushing the message into the global error state
      // stacked the red "Error Occurred" banner on top of the toast — the
      // same text twice, per the one-surface rule in CLAUDE.md.
      toast.error(message);
    }
  };

  // Rotation's render-mode default (cutout vs. in-scene) is read off the
  // reference product: the sync guess from its alpha channel first, refined
  // by the edge-pixel sample for opaque images.
  const rotationReferenceImage = React.useMemo(
    () =>
      (studioProducts.find((p) => p.isSelected) ?? studioProducts[0])
        ?.originalImage,
    [studioProducts]
  );
  const applyRotationDefaultMode = React.useCallback(
    (referenceImage: NonNullable<typeof rotationReferenceImage>) => {
      setStudioRotationMode(inferRotationDefaultMode(referenceImage));
      if (!referenceImage.hasAlpha) {
        inferRotationDefaultModeAsync(referenceImage)
          .then((mode) => {
            if (mode === "cutout") setStudioRotationMode("cutout");
          })
          .catch(() => {});
      }
    },
    [setStudioRotationMode]
  );

  const handleImageTypeChange = React.useCallback(
    (next: string) => {
      if (
        next === "Rotation" &&
        studioImageType !== "Rotation" &&
        rotationReferenceImage
      ) {
        applyRotationDefaultMode(rotationReferenceImage);
      }
      setStudioImageType(next);
    },
    [
      studioImageType,
      rotationReferenceImage,
      applyRotationDefaultMode,
      setStudioImageType,
      navigate,
      projectId,
    ]
  );

  // The empty project's goal cards (SIS-565) let a user choose Rotation
  // before any product exists, so the transition above finds no reference
  // image. Repeat the inference once when the first reference image arrives
  // while Rotation is active; later product changes leave a manual choice
  // alone, exactly like the transition path.
  const hadRotationReferenceRef = React.useRef(Boolean(rotationReferenceImage));
  React.useEffect(() => {
    const had = hadRotationReferenceRef.current;
    hadRotationReferenceRef.current = Boolean(rotationReferenceImage);
    if (had || !rotationReferenceImage || studioImageType !== "Rotation")
      return;
    applyRotationDefaultMode(rotationReferenceImage);
  }, [rotationReferenceImage, studioImageType, applyRotationDefaultMode]);

  // Empty project (SIS-565): a goal card sets the mode exactly like the
  // Studio panel's picker, so section 1 is already right once the first
  // product arrives. It deliberately does not open the file dialog: a picker
  // that jumps straight into the OS dialog leaves the user without any
  // explanation. Instead a callout above the dropzone says what to do next,
  // and the dropzone is scrolled into view and highlighted.
  const [goalPickCount, setGoalPickCount] = React.useState(0);
  const handleGoalSelect = React.useCallback(
    (entry: CreationCatalogEntry) => {
      trackCreationGoalSelected({
        goalId: entry.id,
        surface: "empty_project",
        projectId,
      });
      // Any running tour hands over to the flow the chosen goal belongs to,
      // rather than staying on a flow whose surface the user just left with
      // nothing there to guide. This runs for every goal, in both
      // directions: the tabs are reachable from a running images tour, and
      // the Studio is reachable from a running Highlights or Video one.
      // `quickStartStage` is only derived for the images flow and is null
      // otherwise, which is exactly what the skip event should carry.
      const nextFlow = flowForGoal(entry);
      if (quickStartFlow && quickStartFlow !== nextFlow) {
        trackQuickstartSkipped(quickStartStage, quickStartFlow);
        startQuickStart(nextFlow);
        trackQuickstartStarted(nextFlow);
      }
      if (entry.surface !== "studio") {
        // Highlights and Video live on their own tabs.
        navigate({
          to:
            entry.surface === "highlights"
              ? "/projects/$projectId/highlights"
              : "/projects/$projectId/video",
          params: { projectId },
        });
        return;
      }
      handleImageTypeChange(entry.imageType);
      setGoalPickCount((n) => n + 1);
    },
    [
      handleImageTypeChange,
      navigate,
      projectId,
      quickStartFlow,
      quickStartStage,
      startQuickStart,
    ]
  );

  // `?mode=` (onboarding goal step, SIS-566): apply once, then drop the
  // param with `replace` so a reload or back-navigation does not re-apply
  // it. Unknown values are dropped silently. A valid mode also counts as a
  // goal pick, so the empty project opens with the callout and the dropzone
  // already in place instead of asking for the same choice a second time.
  const { mode: deepLinkMode } = useSearch({ strict: false }) as {
    mode?: string;
  };
  const deepLinkConsumedRef = React.useRef(false);
  React.useEffect(() => {
    if (deepLinkMode === undefined) {
      // Param gone: the next arrival (a later client-side navigation onto
      // this retained component) is a fresh one.
      deepLinkConsumedRef.current = false;
      return;
    }
    if (deepLinkConsumedRef.current) return;
    deepLinkConsumedRef.current = true;
    const imageType = resolveDeepLinkMode(deepLinkMode);
    if (imageType) {
      handleImageTypeChange(imageType);
      setGoalPickCount((n) => n + 1);
      setArrivedWithGoal(true);
    }
    navigate({
      to: "/projects/$projectId",
      params: { projectId },
      search: (prev) => ({ ...prev, mode: undefined }),
      replace: true,
    });
  }, [deepLinkMode, handleImageTypeChange, navigate, projectId]);
  // After a pick the upload group moves above the card grid and slides in,
  // so it sits where the user is looking instead of below the fold. The
  // studio content scrolls back to the very top so the heading above the
  // group stays visible (aligning the group itself cut the heading off);
  // deferred a frame so the scroll targets the settled layout.
  const studioScrollRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (goalPickCount === 0) return;
    const id = window.requestAnimationFrame(() => {
      studioScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [goalPickCount]);
  // The callout needs a catalog goal to talk about; a workflow picked in
  // the panel after a card click hides it again.
  const goalCalloutVisible = goalPickCount > 0 && currentGoal !== undefined;

  // Arriving from the onboarding with a goal (`?mode=`) shows nothing but
  // the dropzone, with the controls collapsed: one instruction instead of
  // prompt, callout, cards and panel all at once. The first upload ends the
  // stage and everything returns. Picking a card in the empty project does
  // not enter this mode; that path keeps its callout and cards.
  const [arrivedWithGoal, setArrivedWithGoal] = React.useState(false);
  const focusedUpload =
    quickStartStage === "upload" && arrivedWithGoal && goalCalloutVisible;

  // The goal gallery is for the first contact. From the second project on
  // the empty state is just the dropzone again: the user knows the panel's
  // picker, and the gallery only made every new project look busy.
  // Two capped project ids answer that, and nothing else does: reading the
  // count off ListUserProjects made an account-wide fetch of every project,
  // every product and each product's latest image a precondition for
  // painting an empty page (SIS-566 review). Asked only where the answer
  // can change what renders — the project is empty, and the focused upload
  // has not already committed to the dropzone.
  const { data: goalGalleryEligibility, isLoading: isGoalGalleryLoading } =
    useGetGoalGalleryEligibility({
      enabled:
        !!auth.currentUser &&
        !focusedUpload &&
        !isLoadingProducts &&
        studioProducts.length === 0 &&
        multiProductScenes.length === 0,
    });
  const showGoalGallery =
    goalGalleryEligibility !== undefined &&
    (goalGalleryEligibility.user?.projectIdsCapped.length ?? 0) <= 1;
  // The fresh onboarding navigates straight into the project and never
  // passes the dashboard, so for the one user the gallery exists for the
  // answer is always a round-trip away. Rendering the plain dropzone in
  // the meantime and swapping in the gallery afterwards is the very flash
  // this is for, so the empty state waits instead. The focused upload has
  // already decided to show the dropzone and never waits; on a failed
  // query `isLoading` clears with no data and the dropzone is the fallback.
  const isGoalGalleryUndecided = !focusedUpload && isGoalGalleryLoading;
  const collapsedForFocusRef = React.useRef(false);
  React.useEffect(() => {
    if (focusedUpload) {
      collapsedForFocusRef.current = true;
      setIsSidebarCollapsed(true);
    } else if (collapsedForFocusRef.current) {
      collapsedForFocusRef.current = false;
      setIsSidebarCollapsed(false);
    }
  }, [focusedUpload, setIsSidebarCollapsed]);
  // Leaving the page (another tab) while focused must not leave the shared
  // sidebar collapsed for whatever renders next.
  React.useEffect(
    () => () => {
      if (collapsedForFocusRef.current) setIsSidebarCollapsed(false);
    },
    [setIsSidebarCollapsed]
  );
  const CurrentGoalIcon = currentGoal ? CREATION_ICONS[currentGoal.icon] : null;

  const bulkGeneratingRef = React.useRef(false);
  const [isBulkGenerating, setIsBulkGenerating] = React.useState(false);

  const handleGenerateForSelectedPlants = async (
    arg?: "1K" | "2K" | "4K" | React.MouseEvent,
    imageGeneration?: ImageGenerationSettings,
    workflowOverride?: Workflow
  ) => {
    // Re-click guard: the header buttons go busy (and drop onClick) for the
    // whole run, but this ref is a synchronous backstop so a second invocation
    // can't loop over already-in-flight products and double-charge them
    // (handleGenerateForSinglePlant has no per-product in-flight guard).
    if (bulkGeneratingRef.current) return;

    const size: "1K" | "2K" | "4K" =
      typeof arg === "string" ? (arg as "1K" | "2K" | "4K") : "1K";

    const selected = studioProducts.filter((p) => p.isSelected);
    if (selected.length === 0) return;
    const quickStartTargetId =
      quickStartStage === "generate" ? selected[0]?.id : undefined;
    if (quickStartTargetId) {
      startQuickStartGeneration(quickStartTargetId);
    }

    bulkGeneratingRef.current = true;
    setIsBulkGenerating(true);
    let quickStartGenerationSucceeded = false;
    try {
      for (const p of selected) {
        const result = await handleGenerateForSinglePlant(
          p.id,
          size,
          undefined,
          imageGeneration || studioImageGeneration,
          workflowOverride
        );
        if (p.id === quickStartTargetId && result.ok) {
          quickStartGenerationSucceeded = true;
        }
      }
    } finally {
      bulkGeneratingRef.current = false;
      setIsBulkGenerating(false);
    }

    if (quickStartTargetId) {
      completeQuickStartGeneration(
        quickStartTargetId,
        quickStartGenerationSucceeded
      );
    }
  };

  // Sequential and awaited: each file goes through the backend `exportImage`
  // endpoint, so the button has to stay busy until the last one is actually
  // delivered rather than returning the instant the requests are fired.
  const { assets: studioExportAssets, imageItems: studioExportImages } =
    studioExportSelection(studioProducts, projectId);
  const handleBulkDownload = async (format: "png" | "jpeg" | "mp4") => {
    for (const content of studioExportAssets) {
      const outputFormat = content.video
        ? "mp4"
        : format === "mp4"
          ? "png"
          : format;
      await handleDownloadImage(
        content.video || content.image || content.storagePath!,
        `${content.title || `image_${content.id}`}.${outputFormat}`,
        outputFormat
      );
    }
  };

  const isCustomWorkflow = ![
    "Standard",
    "Multi-Product",
    "Decoration",
    "Scaling",
    "Re-Coloring",
    "Rotation",
    "Cutout",
    "Highlights",
    "Transform Technical Drawing",
    "Details",
    "Aspect Ratio",
  ].includes(studioImageType);
  const resolvedRemoveBg = resolveBackgroundRemovalConfig({
    backgroundRemoval: studioBackgroundRemoval,
    cutoutShadows: studioCutoutShadows,
  });
  const isPngCutoutMode =
    studioImageType === "Cutout" && resolvedRemoveBg.mode === "simple";
  const isScalingMode = studioImageType === "Scaling";
  const isMultiProductMode = studioImageType === "Multi-Product";
  const selectedCount = studioProducts.filter((p) => p.isSelected).length;
  // In Dimensional Drawing mode, block the batch generate button when any
  // selected product is missing the dimension(s) required by the active marker.
  const hasIncompleteSelectedScaling =
    isScalingMode &&
    studioProducts.some(
      (p) =>
        p.isSelected &&
        !scalingDimensionsComplete(
          p,
          activeDimensionPreset?.markerPreference ?? dimensionMarker
        )
    );

  // Callout (after a pick) plus dropzone. Rendered above the card grid once
  // a goal is picked, below it before, so there is a single file input.
  const renderEmptyStateDropzone = (sizeClassName: string) => (
    <div
      className={`relative flex cursor-pointer ${sizeClassName} flex-col items-center justify-center rounded-xl border-2 border-dashed transition-colors ${
        goalCalloutVisible ? "mt-3" : ""
      } ${
        isDragOver
          ? "border-blue-400 bg-blue-50/80"
          : "border-gray-300 bg-gray-50/50 hover:border-gray-400"
      } ${
        quickStartStage === "upload" || goalCalloutVisible
          ? "ring-4 ring-blue-500/25 border-blue-400 bg-blue-50/40"
          : ""
      }`}
      role="button"
      tabIndex={0}
      aria-label={t("studio.emptyDragDrop")}
      onClick={() => emptyStateUploadRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          emptyStateUploadRef.current?.click();
        }
      }}
    >
      <input
        ref={emptyStateUploadRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        className="hidden"
        onChange={handleStudioImageUpload}
      />
      <PhotoIcon
        className={`w-12 h-12 mb-3 transition-colors ${
          isDragOver ? "text-blue-500 opacity-60" : "text-gray-400 opacity-30"
        }`}
      />
      <p className="font-semibold text-gray-700 mb-1">
        {currentGoal
          ? t("studio.emptyUploadForGoal", {
              goal: t(currentGoal.labelKey),
            })
          : t("studio.emptyUploadTitle")}
      </p>
      <p
        className={`text-sm font-medium ${
          isDragOver ? "text-blue-600" : "text-gray-500"
        }`}
      >
        {t("studio.emptyDragDrop")}
      </p>
      <p className="text-xs text-gray-400 mt-1">{t("studio.emptyOrUpload")}</p>
    </div>
  );

  const emptyStateUploadGroup = (
    <div
      className={
        goalCalloutVisible
          ? "mb-6 animate-in fade-in slide-in-from-top-2 duration-300"
          : "mt-6"
      }
    >
      {goalCalloutVisible && currentGoal && CurrentGoalIcon && (
        <div
          role="status"
          className="flex flex-col gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white text-blue-700 shadow-sm">
              <CurrentGoalIcon className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {t("studio.goalPickedTitle", {
                  goal: t(currentGoal.labelKey),
                })}
              </p>
              <p className="mt-0.5 text-xs leading-5 text-gray-700">
                {t("studio.goalPickedDesc")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => emptyStateUploadRef.current?.click()}
            className="inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            <PhotoIcon className="h-4 w-4" />
            {t("studio.goalPickedUpload")}
          </button>
        </div>
      )}
      {renderEmptyStateDropzone("min-h-[180px]")}
    </div>
  );
  return (
    <div className="flex flex-grow overflow-hidden relative">
      {isQuickStartCelebrating && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/20 p-6 backdrop-blur-[1px]">
          <QuickStartPrompt
            eyebrow={t("quickStart.studioEyebrow")}
            title={t("quickStart.completeTitle")}
            description={t("quickStart.completeDesc")}
            className="w-full max-w-md"
            onSkip={finalizeQuickStart}
            actionLabel={t("quickStart.completeAction")}
          />
        </div>
      )}
      {/* Sidebar - only visible here */}
      <div
        className={`flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden border-r border-gray-200 bg-white relative ${isSidebarCollapsed ? "w-0" : "w-[360px]"}`}
      >
        <div className="w-[360px] h-full flex flex-col">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-white z-10">
            <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              Controls
            </span>
            <button
              onClick={() => setIsSidebarCollapsed(true)}
              className="p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 rounded-lg transition-colors"
              title="Collapse sidebar"
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-grow overflow-y-auto">
            {quickStartStage === "configure" && (
              <QuickStartPrompt
                eyebrow={t("quickStart.studioEyebrow")}
                title={t("quickStart.configureTitle")}
                description={t("quickStart.configureDesc")}
                className="m-4 mb-0"
                onSkip={skipQuickStartFromCurrent}
              />
            )}
            <PanelErrorBoundary
              panel="studio-panel"
              resetKeys={[projectId, studioImageType]}
            >
              <StudioPanel
                imageGeneration={studioImageGeneration}
                onImageGenerationChange={setStudioImageGeneration}
                vibe={studioVibe}
                onVibeChange={handleQuickStartVibeChange}
                vibes={vibes}
                scene={studioScene}
                onSceneChange={handleQuickStartSceneChange}
                scenes={scenes}
                aspectRatio={studioAspectRatio}
                onAspectRatioChange={setStudioAspectRatio}
                vibeContext={studioVibeContext}
                onVibeContextChange={setStudioVibeContext}
                useTrueToneStudio={useTrueToneStudio}
                onUseTrueToneStudioChange={setUseTrueToneStudio}
                productLightsOn={productLightsOn}
                onProductLightsOnChange={setProductLightsOn}
                productLightsWarmth={productLightsWarmth}
                onProductLightsWarmthChange={setProductLightsWarmth}
                decorationSurfaceOverride={studioDecorationSurfaceOverride}
                onDecorationSurfaceOverrideChange={
                  setStudioDecorationSurfaceOverride
                }
                decorationFramingPreference={studioDecorationFramingPreference}
                onDecorationFramingPreferenceChange={
                  setStudioDecorationFramingPreference
                }
                decorationRoomPlacementPreference={
                  studioDecorationRoomPlacementPreference
                }
                onDecorationRoomPlacementPreferenceChange={
                  setStudioDecorationRoomPlacementPreference
                }
                decorationViewAngle={studioDecorationViewAngle}
                onDecorationViewAngleChange={setStudioDecorationViewAngle}
                retailerProfiles={retailerProfiles}
                selectedProfileId={studioSelectedProfileId}
                onProfileChange={setStudioSelectedProfileId}
                imageType={studioImageType}
                onImageTypeChange={handleImageTypeChange}
                colorSwatches={colorSwatches}
                savedColorSwatches={savedColorSwatches}
                onAddSwatch={handleAddSwatch}
                onRemoveSwatch={handleRemoveSwatch}
                onToggleSwatch={handleToggleSwatch}
                onUpdateSwatch={handleUpdateSwatch}
                recolorOptions={studioRecolorOptions}
                onRecolorOptionsChange={setStudioRecolorOptions}
                rotationAngle={studioRotationAngle}
                onRotationAngleChange={setStudioRotationAngle}
                rotationMode={studioRotationMode}
                onRotationModeChange={setStudioRotationMode}
                rotationModel={studioRotationModel}
                onRotationModelChange={setStudioRotationModel}
                rotationQuality={studioRotationQuality}
                onRotationQualityChange={setStudioRotationQuality}
                useAvatar={useAvatar}
                onUseAvatarChange={setUseAvatar}
                avatarImage={studioAvatar}
                onAvatarImageChange={setStudioAvatar}
                workflows={workflows}
                technicalDrawingMode={studioTechnicalDrawingMode}
                onTechnicalDrawingModeChange={setStudioTechnicalDrawingMode}
                technicalDrawingMaterialImage={
                  studioTechnicalDrawingMaterialImage
                }
                onTechnicalDrawingMaterialImageChange={
                  setStudioTechnicalDrawingMaterialImage
                }
                technicalDrawingMaterial={studioTechnicalDrawingMaterial}
                onTechnicalDrawingMaterialChange={
                  setStudioTechnicalDrawingMaterial
                }
                technicalDrawingShadowStyle={studioTechnicalDrawingShadowStyle}
                onTechnicalDrawingShadowStyleChange={
                  setStudioTechnicalDrawingShadowStyle
                }
                detailsFocus={studioDetailsFocus}
                onDetailsFocusChange={setStudioDetailsFocus}
                detailsMode={studioDetailsMode}
                onDetailsModeChange={setStudioDetailsMode}
                detailsCameraPerspective={studioDetailsCameraPerspective}
                onDetailsCameraPerspectiveChange={
                  setStudioDetailsCameraPerspective
                }
                detailsMaterial={studioDetailsMaterial}
                onDetailsMaterialChange={setStudioDetailsMaterial}
                detailsMaterialReferenceImage={studioDetailsMaterialReference}
                onDetailsMaterialReferenceImageChange={
                  setStudioDetailsMaterialReference
                }
                cutoutShadows={studioCutoutShadows}
                onCutoutShadowsChange={setStudioCutoutShadows}
                backgroundRemoval={studioBackgroundRemoval}
                onBackgroundRemovalChange={setStudioBackgroundRemoval}
                productDetails={studioProductDetails}
                onAddProductDetail={handleAddProductDetail}
                onRemoveProductDetail={handleRemoveProductDetail}
                onUpdateProductDetail={handleUpdateProductDetail}
                companyLogo={companyLogo}
                onCompanyLogoChange={setCompanyLogo}
                logoPosition={logoPosition}
                onLogoPositionChange={setLogoPosition}
                logoScale={logoScale}
                onLogoScaleChange={setLogoScale}
                dimensionMarker={dimensionMarker}
                dimensionPresetId={dimensionPresetId}
                onDimensionPresetChange={(id) => {
                  setDimensionPresetId(id);
                  setDimensionShowLabel(undefined);
                }}
                dimensionShowLabel={dimensionShowLabel}
                onDimensionShowLabelChange={setDimensionShowLabel}
                onDimensionMarkerChange={setDimensionMarker}
                dimensionCapStyle={dimensionCapStyle}
                onDimensionCapStyleChange={setDimensionCapStyle}
                dimensionTargetFormat={dimensionTargetFormat}
                onDimensionTargetFormatChange={setDimensionTargetFormat}
                quickStartHighlight={
                  quickStartStage === "configure" ? "environment" : undefined
                }
              />
            </PanelErrorBoundary>
          </div>
        </div>
      </div>

      <div
        className={`flex-grow bg-gray-50/50 flex flex-col relative overflow-hidden`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragOver &&
          (studioProducts.length > 0 ||
            multiProductScenes.length > 0 ||
            uploadingFiles.length > 0) && (
            <div
              className="absolute inset-0 z-50 flex items-center justify-center bg-blue-500/10 border-2 border-dashed border-blue-400 rounded-xl m-2 pointer-events-none"
              aria-hidden
            >
              <div className="flex flex-col items-center gap-2 text-blue-700 font-semibold">
                <PhotoIcon className="w-12 h-12" />
                <span>{t("studio.dropImagesHere")}</span>
              </div>
            </div>
          )}
        <header
          className={`relative flex-shrink-0 bg-white border-b border-gray-200 py-4 shadow-sm z-40 flex items-center justify-between px-6`}
        >
          <div className="flex items-center gap-3">
            {isSidebarCollapsed && (
              <button
                onClick={() => setIsSidebarCollapsed(false)}
                className="flex-shrink-0 w-8 h-8 bg-white border border-gray-300 rounded-lg flex items-center justify-center text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-all"
                title="Expand sidebar"
              >
                <ArrowRightIcon className="w-4 h-4" />
              </button>
            )}
            <div>
              <h1 className="text-xl tracking-tight font-semibold text-gray-900">
                Studio
              </h1>
              <p className="text-xs text-gray-500 font-medium">
                {studioProducts.length} {t("studio.assets")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isMultiProductMode && (
              <div className="relative" ref={selectionMenuRef}>
                <button
                  onClick={() => setIsSelectionMenuOpen(!isSelectionMenuOpen)}
                  className="h-10 px-4 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 flex items-center justify-center gap-2 transition-colors"
                >
                  {t("studio.selection")}
                  <ChevronDownIcon className="w-4 h-4" />
                </button>
                {isSelectionMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-20 animate-in fade-in zoom-in-95 duration-100">
                    <div className="p-1">
                      <div className="relative">
                        <button
                          onClick={() =>
                            setIsQuickSelectOpen(!isQuickSelectOpen)
                          }
                          className="w-full px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg flex items-center justify-between gap-2 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <CursorArrowRaysIcon className="w-4 h-4" />
                            {t("studio.quickSelect")}
                          </div>
                          <ChevronDownIcon
                            className={`w-4 h-4 transition-transform ${isQuickSelectOpen ? "rotate-180" : ""}`}
                          />
                        </button>
                        {isQuickSelectOpen && (
                          <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-xl">
                            <label className="text-xs font-semibold text-gray-700 block mb-1">
                              {t("studio.enterFilenamesNoExt")}
                            </label>
                            <textarea
                              value={quickSelectInput}
                              onChange={(e) =>
                                setQuickSelectInput(e.target.value)
                              }
                              rows={4}
                              className="w-full p-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                              placeholder={t("studio.quickSelectPlaceholder")}
                            />
                            <button
                              onClick={() => {
                                const ids = quickSelectInput
                                  .split(/[\s,;\n]+/)
                                  .filter((id) => id.trim() !== "");
                                if (ids.length > 0) {
                                  setStudioProducts((prev) =>
                                    prev.map((p) => {
                                      const filename =
                                        p.originalImage.name.split(".")[0];
                                      return {
                                        ...p,
                                        isSelected: ids.includes(filename),
                                      };
                                    })
                                  );
                                }
                                setIsQuickSelectOpen(false);
                                setIsSelectionMenuOpen(false);
                                setQuickSelectInput("");
                              }}
                              className="mt-2 w-full px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                            >
                              {t("studio.applySelection")}
                            </button>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setStudioProducts((prev) =>
                            prev.map((p) => ({ ...p, isSelected: true }))
                          );
                          setIsSelectionMenuOpen(false);
                        }}
                        className="w-full px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2 transition-colors"
                      >
                        {t("studio.selectAll")}
                      </button>
                      <button
                        onClick={() => {
                          setStudioProducts((prev) =>
                            prev.map((p) => ({ ...p, isSelected: false }))
                          );
                          setIsSelectionMenuOpen(false);
                        }}
                        className="w-full px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2 transition-colors"
                      >
                        {t("studio.deselectAll")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!isMultiProductMode && (
              <div className="h-8 w-px bg-gray-200 mx-1"></div>
            )}

            {!isMultiProductMode && (
              <div>
                {quickStartStage === "generate" && (
                  <QuickStartPrompt
                    eyebrow={t("quickStart.studioEyebrow")}
                    title={t("quickStart.generateTitle")}
                    description={t("quickStart.generateDesc")}
                    className="absolute right-6 top-[72px] z-50 w-80"
                    onSkip={skipQuickStartFromCurrent}
                  />
                )}
                {quickStartStage === "generating" && (
                  <QuickStartPrompt
                    eyebrow={t("quickStart.studioEyebrow")}
                    title={t("quickStart.generatingTitle")}
                    description={t("quickStart.generatingDesc")}
                    className="absolute right-6 top-[72px] z-50 w-80"
                    onSkip={skipQuickStartFromCurrent}
                  />
                )}
                {quickStartStage === "view_result" && (
                  <QuickStartPrompt
                    eyebrow={t("quickStart.studioEyebrow")}
                    title={t("quickStart.viewResultTitle")}
                    description={t("quickStart.viewResultDesc")}
                    className="absolute right-6 top-[72px] z-50 w-80"
                    onSkip={skipQuickStartFromCurrent}
                  />
                )}
                {isCustomWorkflow ? (
                  <WorkflowRunButton
                    workflow={workflows.find((w) => w.id === studioImageType)}
                    inputs={studioProducts
                      .filter((p) => p.isSelected)
                      .map((p) => p.image)}
                    variant="primary"
                    size="md"
                    centered
                    responsiveLabel="lg"
                    aria-label={`${t("studio.runWorkflowButton")} (${selectedCount})`}
                    busy={isBulkGenerating}
                    busyWords="standard"
                    disabled={selectedCount === 0}
                    onGenerate={(snapshot) =>
                      void handleGenerateForSelectedPlants(
                        "1K",
                        undefined,
                        snapshot
                      )
                    }
                    label={`${t("studio.runWorkflowButton")} (${selectedCount})`}
                    className={
                      quickStartStage === "generate"
                        ? "ring-4 ring-blue-500/30"
                        : undefined
                    }
                  />
                ) : isPngCutoutMode ? (
                  <ImageGenerationButton
                    showSettings={false}
                    variant="primary"
                    size="md"
                    responsiveLabel="lg"
                    aria-label={`${t("studio.removeBackground")} (${selectedCount})`}
                    busy={isBulkGenerating}
                    busyWords="bg"
                    disabled={selectedCount === 0}
                    onGenerate={() =>
                      void handleGenerateForSelectedPlants("1K")
                    }
                    label={`${t("studio.removeBackground")} (${selectedCount})`}
                    fixedCredit={Number(
                      selectedCount > 0
                        ? (selectedCount * COST_BG_REMOVE).toFixed(0)
                        : undefined
                    )}
                    className={
                      quickStartStage === "generate"
                        ? "ring-4 ring-blue-500/30"
                        : undefined
                    }
                  />
                ) : isScalingMode ? (
                  <ImageGenerationButton
                    showSettings={false}
                    variant="primary"
                    size="md"
                    responsiveLabel="lg"
                    aria-label={`${t("studio.generate")} (${selectedCount})`}
                    busy={isBulkGenerating}
                    busyWords="standard"
                    disabled={
                      selectedCount === 0 || hasIncompleteSelectedScaling
                    }
                    onGenerate={() =>
                      void handleGenerateForSelectedPlants("1K")
                    }
                    label={`${t("studio.generate")} (${selectedCount})`}
                    fixedCredit={Number(
                      selectedCount > 0
                        ? (selectedCount * COST_DIMENSION).toFixed(0)
                        : undefined
                    )}
                    title={
                      hasIncompleteSelectedScaling
                        ? t("studio.scalingDimensionRequiredError")
                        : undefined
                    }
                    className={
                      quickStartStage === "generate"
                        ? "ring-4 ring-blue-500/30"
                        : undefined
                    }
                  />
                ) : (
                  <GenerateButtonDropdown
                    variant="primary"
                    size="md"
                    responsiveLabel="lg"
                    ariaLabel={`${t("studio.generate")} (${selectedCount})`}
                    busy={isBulkGenerating}
                    busyWords="standard"
                    label={`${t("studio.generate")} (${selectedCount})`}

                    settings={studioImageGeneration}
                    requireAspectRatio={studioImageType === "Aspect Ratio"}
                    preserveAspectRatio={studioImageType === "Re-Coloring"}
                    requiresSunburst={
                      studioImageType === "Re-Coloring" &&
                      !!studioRecolorOptions.grainSketch
                    }
                    onSettingsChange={(next) => {
                      setStudioImageGeneration(next);
                      if (next.aspectRatio && next.aspectRatio !== "original")
                        setStudioAspectRatio(next.aspectRatio);
                    }}
                    recommendSunburst
                    onGenerate={(quality, _mode, selected) => {
                      void handleGenerateForSelectedPlants(quality, selected);
                    }}
                    disabled={selectedCount === 0}
                    fullWidth={false}
                    className={
                      quickStartStage === "generate"
                        ? "ring-4 ring-blue-500/30"
                        : undefined
                    }

                    creditMultiplier={
                      selectedCount *
                      (studioImageType === "Re-Coloring"
                        ? Math.max(
                            1,
                            colorSwatches.filter((s) => s.isSelected).length
                          )
                        : 1)
                    }
                  />
                )}
              </div>
            )}

            {!isMultiProductMode && (
              <AssetExportMenu
                assets={studioExportAssets}
                onBulkDownload={handleBulkDownload}
                trigger="primary"
                triggerLabel={t("assetExport.downloadCount", {
                  count: studioExportAssets.length,
                })}
                aiWatermark={aiWatermarkEnabled}
                onToggleAiWatermark={() =>
                  setAiWatermarkEnabled(!aiWatermarkEnabled)
                }
                aiWatermarkDisabled={exportSettingsLoading}
                onPushToShopvibes={
                  studioExportImages.length
                    ? () =>
                        setExportDialog({
                          kind: "shopvibes",
                          items: studioExportImages,
                        })
                    : undefined
                }
                onExportLinkList={
                  studioExportImages.length
                    ? () =>
                        setExportDialog({
                          kind: "links",
                          items: studioExportImages,
                        })
                    : undefined
                }
              />
            )}
          </div>
        </header>

        <div
          ref={studioScrollRef}
          className={`flex-grow overflow-y-auto ${isSidebarCollapsed ? "px-12 py-6" : "p-6"}`}
        >
          {linkingState.active && (
            <div className="sticky -top-6 z-20 mb-4 p-3 bg-blue-100 border-y-2 border-blue-300 flex items-center justify-between -mx-6 px-6 shadow-sm">
              <p className="font-semibold text-blue-800 text-sm">
                {t("studio.linkingProductsToHint", {
                  filename:
                    studioProducts.find(
                      (p) => p.id === linkingState.sourceProductId
                    )?.originalImage.name ?? "",
                  max: MAX_MULTI_PRODUCT_SCENE_PRODUCTS,
                })}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancelLinking}
                  className="px-3 py-1 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={handleSaveLinks}
                  disabled={
                    linkingState.selectedIds.size < 2 ||
                    linkingState.selectedIds.size >
                      MAX_MULTI_PRODUCT_SCENE_PRODUCTS
                  }
                  className="px-3 py-1 text-xs font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
                >
                  {t("studio.saveLinksWithCount", {
                    count: linkingState.selectedIds.size,
                  })}
                </button>
              </div>
            </div>
          )}

          {isLoadingProducts ||
          (studioProducts.length === 0 &&
            multiProductScenes.length === 0 &&
            isLoadingProducts) ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 animate-pulse">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-[440px]"
                >
                  <div className="p-2.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                    <div className="h-4 w-32 bg-gray-200 rounded"></div>
                    <div className="h-4 w-4 bg-gray-200 rounded-full"></div>
                  </div>
                  <div className="h-56 bg-gray-100 border-b border-gray-100"></div>
                  <div className="p-3 flex-grow space-y-2.5">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="h-8 bg-gray-100 rounded"></div>
                      <div className="h-8 bg-gray-100 rounded"></div>
                    </div>
                    <div className="h-8 bg-gray-100 rounded"></div>
                    <div className="h-10 bg-gray-200 rounded mt-auto"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : studioProducts.length > 0 ||
            multiProductScenes.length > 0 ||
            uploadingFiles.length > 0 ? (
            <div className="space-y-6 pb-10">
              {/* View Toggle */}
              {multiProductScenes.length > 0 && studioProducts.length > 0 && (
                <div className="flex items-center justify-between mb-4">
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setStudioViewMode("all")}
                      className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all flex items-center gap-2 ${studioViewMode === "all" ? "bg-white text-black shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                    >
                      {t("studio.viewAll")}
                    </button>
                    <button
                      onClick={() => setStudioViewMode("scenes")}
                      className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all flex items-center gap-2 ${studioViewMode === "scenes" ? "bg-white text-black shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                    >
                      <Squares2X2Icon className="w-4 h-4" />
                      {t("studio.scenesWithCount", {
                        count: multiProductScenes.length,
                      })}
                    </button>
                    <button
                      onClick={() => setStudioViewMode("products")}
                      className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all flex items-center gap-2 ${studioViewMode === "products" ? "bg-white text-black shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                    >
                      <PhotoIcon className="w-4 h-4" />
                      {t("studio.productsWithCount", {
                        count: studioProducts.length,
                      })}
                    </button>
                  </div>
                  <UploadMenu
                    onImagesSelected={handleMenuImagesSelected}
                    onExcelSelected={handleMenuExcelSelected}
                  />
                </div>
              )}

              {/* Scenes Grid */}
              {multiProductScenes.length > 0 &&
                (studioViewMode === "all" || studioViewMode === "scenes") && (
                  <div>
                    {(studioViewMode === "all" ||
                      studioProducts.length === 0) && (
                      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <Squares2X2Icon className="w-4 h-4" />
                        Multi-Product Scenes ({multiProductScenes.length})
                      </h3>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                      {multiProductScenes.map((scene) => {
                        const sceneIsAnalyzing = scene.products.some((sp) => {
                          const product = studioProducts.find(
                            (p) => p.id === sp.productId
                          );
                          return product?.analysisStatus === "pending";
                        });
                        return (
                          <div
                            key={scene.id}
                            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full transition-all duration-200 group/card hover:border-gray-300 hover:shadow-md"
                          >
                            {/* Header — match product card: p-3, bg-white, single line */}
                            <div className="p-2.5 border-b border-gray-100 bg-white flex items-center justify-between flex-shrink-0">
                              <div className="flex items-center gap-3 overflow-hidden min-w-0">
                                <span
                                  className="font-medium text-gray-700 text-sm truncate"
                                  title={`${scene.name} · ${scene.products.length} products`}
                                >
                                  {scene.name}
                                </span>
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                  {scene.products.length} products
                                </span>
                              </div>
                              <button
                                onClick={() => handleDeleteScene(scene.id)}
                                className="text-gray-400 hover:text-red-500 p-1 rounded-full hover:bg-red-50 opacity-0 group-hover/card:opacity-100 transition-opacity focus-visible:opacity-100"
                                title="Delete Scene"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                            {/* Preview area — match product card image block height and borders */}
                            <div
                              className={`relative h-56 p-3 flex items-center justify-center border-b border-gray-100 group flex-shrink-0 ${
                                scene.generatedContent.length > 0
                                  ? "cursor-pointer"
                                  : ""
                              }`}
                              style={{
                                backgroundImage:
                                  "linear-gradient(45deg, #f3f4f6 25%, transparent 25%), linear-gradient(-45deg, #f3f4f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f3f4f6 75%), linear-gradient(-45deg, transparent 75%, #f3f4f6 75%)",
                                backgroundSize: "16px 16px",
                                backgroundPosition:
                                  "0 0, 0 8px, 8px -8px, -8px 0px",
                                backgroundColor: "#fafafa",
                              }}
                              onClick={(e) => {
                                if (scene.generatedContent.length === 0) return;
                                e.stopPropagation();
                                handleViewSceneImage(
                                  scene.id,
                                  scene.generatedContent[0].id
                                );
                              }}
                              title={
                                scene.generatedContent.length > 0
                                  ? t("studio.results")
                                  : undefined
                              }
                            >
                              <div className="grid grid-cols-2 gap-2 w-full h-full max-w-[200px] max-h-[200px] transition-transform duration-200 group-hover:scale-[1.02]">
                                {scene.products.slice(0, 4).map((sp) => {
                                  const product = studioProducts.find(
                                    (p) => p.id === sp.productId
                                  );
                                  if (!product) return null;
                                  return (
                                    <div
                                      key={sp.productId}
                                      className="relative w-full aspect-square bg-white/90 rounded-xl border border-gray-200 shadow-sm p-1 overflow-hidden"
                                    >
                                      <img
                                        src={getImageSrc(product.image)}
                                        alt={product.originalImage.name}
                                        className="w-full h-full object-contain"
                                      />
                                      <div className="absolute -top-0.5 -right-0.5 bg-blue-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full shadow-sm">
                                        {sp.quantity}x
                                      </div>
                                    </div>
                                  );
                                })}
                                {scene.products.length > 4 && (
                                  <div className="w-full aspect-square bg-white/90 rounded-xl border border-gray-200 shadow-sm flex items-center justify-center text-xs font-semibold text-gray-500">
                                    +{scene.products.length - 4}
                                  </div>
                                )}
                              </div>

                              {/* Results overlay badge — mirrors StudioProductCard */}
                              {scene.generatedContent.length > 0 && (
                                <div
                                  className="absolute bottom-3 left-3 z-20 group/results"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="relative">
                                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/90 backdrop-blur-sm text-gray-700 text-[10px] font-semibold rounded-lg cursor-pointer shadow-sm border border-gray-200 hover:bg-white transition-colors">
                                      <SparklesIcon className="w-3 h-3" />
                                      <span>
                                        {scene.generatedContent.length}
                                      </span>
                                    </div>
                                    <div className="absolute bottom-full left-0 mb-2 opacity-0 invisible group-hover/results:opacity-100 group-hover/results:visible transition-all duration-200 transform translate-y-2 group-hover/results:translate-y-0">
                                      <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-2 min-w-[180px]">
                                        <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider px-1 mb-1.5">
                                          {t("studio.results")} (
                                          {scene.generatedContent.length})
                                        </p>
                                        <div className="flex flex-wrap gap-1.5 max-w-[200px]">
                                          {scene.generatedContent.map(
                                            (content) => (
                                              <button
                                                key={content.id}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleViewSceneImage(
                                                    scene.id,
                                                    content.id
                                                  );
                                                }}
                                                className="relative w-10 h-10 flex-shrink-0 rounded-xl overflow-hidden border border-gray-200 hover:border-blue-500 hover:scale-105 transition-all shadow-sm"
                                              >
                                                <img
                                                  src={
                                                    content.thumbnailUrl ||
                                                    content.image ||
                                                    ""
                                                  }
                                                  className="w-full h-full object-cover"
                                                  alt="Scene result"
                                                  loading="lazy"
                                                />
                                              </button>
                                            )
                                          )}
                                        </div>
                                      </div>
                                      <div className="absolute -bottom-1.5 left-4 w-3 h-3 bg-white border-r border-b border-gray-200 transform rotate-45" />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            {/* Footer — match product card: p-4, border-t, same button styles */}
                            <div className="p-3 flex flex-col gap-2.5 bg-white flex-grow">
                              <div
                                className="mt-auto flex items-center gap-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex-1 min-w-0">
                                  <ImageGenerationButton
                                    value={studioImageGeneration}
                                    onChange={setStudioImageGeneration}
                                    recommendSunburst
                                    variant="secondary"
                                    size="md"
                                    fullWidth
                                    busy={
                                      scene.isGenerating || sceneIsAnalyzing
                                    }
                                    busyWords="standard"
                                    busyLabel={
                                      sceneIsAnalyzing && !scene.isGenerating
                                        ? t("studio.analyzing")
                                        : undefined
                                    }
                                    disabled={
                                      scene.isGenerating || sceneIsAnalyzing
                                    }
                                    onGenerate={() =>
                                      handleGenerateForScene(
                                        scene.id,
                                        studioImageGeneration.resolution
                                      )
                                    }
                                    title={
                                      sceneIsAnalyzing
                                        ? t("studio.waitingForAnalysis")
                                        : undefined
                                    }
                                    label={t("studio.generateScene")}
                                  />
                                </div>
                                <button
                                  onClick={() =>
                                    setConfiguringSceneId(scene.id)
                                  }
                                  title={t("studio.configureScene")}
                                  aria-label={t("studio.configureScene")}
                                  className="h-10 w-10 shrink-0 inline-flex items-center justify-center text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-colors"
                                >
                                  <PencilSquareIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              {/* Products Grid */}
              {(studioProducts.length > 0 || uploadingFiles.length > 0) &&
                (studioViewMode === "all" || studioViewMode === "products") && (
                  <div>
                    {(studioViewMode === "all" ||
                      multiProductScenes.length === 0) && (
                      <div className="flex items-center justify-between gap-2 mb-4">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                          <PhotoIcon className="w-4 h-4" />
                          {t("studio.productsWithCount", {
                            count: studioProducts.length,
                          })}
                        </h3>
                        {multiProductScenes.length === 0 && (
                          <UploadMenu
                            onImagesSelected={handleMenuImagesSelected}
                            onExcelSelected={handleMenuExcelSelected}
                            highlight={quickStartStage === "upload"}
                          />
                        )}
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                      {studioProducts.map((product) => (
                        <div
                          key={product.id}
                          className={
                            quickStartStage === "generate" ||
                            ((quickStartStage === "generating" ||
                              quickStartStage === "view_result") &&
                              quickStartProductId === product.id)
                              ? "rounded-xl ring-4 ring-blue-500/20"
                              : ""
                          }
                        >
                          <AutoSaveProductCard
                            workflow={workflows.find(
                              (w) => w.id === studioImageType
                            )}
                            requiresSunburst={
                              studioImageType === "Re-Coloring" &&
                              !!studioRecolorOptions.grainSketch
                            }
                            imageGeneration={studioImageGeneration}
                            onImageGenerationChange={setStudioImageGeneration}
                            product={product}
                            onSaveStateChange={handleProductSaveStateChange}
                            onSelectionChange={handlePlantSelectionChange}
                            onDimensionsChange={handlePlantDimensionsChange}
                            onInfoChange={handlePlantInfoChange}
                            onGenerate={(
                              id,
                              size,
                              imageGeneration,
                              workflow
                            ) => {
                              if (quickStartStage === "generate") {
                                startQuickStartGeneration(id);
                              }
                              return handleGenerateForSinglePlant(
                                id,
                                size,
                                undefined,
                                imageGeneration,
                                workflow
                              ).then((result) => {
                                if (quickStartStage === "generate") {
                                  completeQuickStartGeneration(id, result.ok);
                                }
                                return result;
                              });
                            }}
                            onViewImage={handlePlantViewImage}
                            onDownloadImage={handleDownloadImage}
                            onDelete={handleStudioProductDelete}
                            onRetryBackgroundRemoval={
                              handleRetryBackgroundRemoval
                            }
                            studioImageType={studioImageType}
                            onStartLinking={handleStartLinking}
                            linkingState={linkingState}
                            onToggleLinkSelection={handleToggleLinkSelection}
                            modelType={modelType}
                            globalContextLength={0}
                            onViewSourceImage={handleViewSourceImage}
                            colorSwatches={colorSwatches}
                            onRetryProductAnalysis={handleRetryProductAnalysis}
                            cutoutOutputFormat={
                              resolvedRemoveBg.mode === "complex"
                                ? "jpeg"
                                : "png"
                            }
                            dimensionMarker={
                              activeDimensionPreset?.markerPreference ??
                              dimensionMarker
                            }
                          />
                        </div>
                      ))}
                      {uploadingFiles.map((entry) => (
                        <div
                          key={entry.id}
                          className="relative bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden animate-pulse"
                        >
                          <div className="aspect-square relative bg-gray-100 overflow-hidden">
                            <img
                              src={entry.previewUrl}
                              alt={entry.name}
                              className="w-full h-full object-contain opacity-50"
                            />
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60">
                              <svg
                                className="w-8 h-8 text-blue-500 animate-spin"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                />
                              </svg>
                              <span className="mt-2 text-sm font-medium text-gray-700">
                                {t("studio.uploading")}
                              </span>
                            </div>
                          </div>
                          <div className="p-3">
                            <p className="text-xs text-gray-400 truncate">
                              {entry.name}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          ) : isGoalGalleryUndecided ? (
            // Held blank rather than filled with a placeholder: the two
            // candidates are a centred dropzone and a card grid, so any
            // stand-in would jump into one of them anyway.
            <div className="mx-auto h-full w-full max-w-6xl pb-10" />
          ) : focusedUpload || !showGoalGallery ? (
            <div className="mx-auto flex h-full w-full max-w-3xl flex-col items-center justify-center pb-10">
              <div className="w-full">
                {renderEmptyStateDropzone("min-h-[320px]")}
              </div>
              {focusedUpload && (
                <button
                  type="button"
                  onClick={skipQuickStartFromCurrent}
                  className="mt-4 rounded-full px-3 py-1 text-xs font-semibold text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                >
                  {t("quickStart.skip")}
                </button>
              )}
            </div>
          ) : (
            <div className="mx-auto w-full max-w-6xl pb-10">
              {quickStartStage === "upload" && (
                <QuickStartPrompt
                  eyebrow={t("quickStart.studioEyebrow")}
                  title={t("quickStart.uploadTitle")}
                  description={
                    currentGoal
                      ? t("quickStart.uploadDesc", {
                          goal: t(currentGoal.labelKey),
                        })
                      : t("quickStart.uploadDescNoGoal")
                  }
                  className="mb-6"
                  onSkip={skipQuickStartFromCurrent}
                />
              )}
              <div className="mb-5">
                <h2 className="text-xl font-semibold tracking-tight text-gray-900">
                  {t("creationCatalog.chooserTitle")}
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  {t("creationCatalog.emptySubtitle")}
                </p>
              </div>
              {goalCalloutVisible && emptyStateUploadGroup}
              <CreationGoalGrid
                selectedId={currentGoal?.id}
                onSelect={handleGoalSelect}
                className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4"
              />
              {!goalCalloutVisible && emptyStateUploadGroup}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {configuringSceneId &&
        (() => {
          const scene = multiProductScenes.find(
            (s) => s.id === configuringSceneId
          );
          if (!scene) return null;
          return (
            <MultiProductSceneConfig
              imageGeneration={studioImageGeneration}
              onImageGenerationChange={setStudioImageGeneration}
              sceneId={configuringSceneId}
              products={studioProducts}
              scene={scene}
              vibes={vibes}
              scenes={scenes}
              onUpdate={handleUpdateSceneConfig}
              onGenerate={handleGenerateForScene}
              onUpdateProductDimensions={handlePlantDimensionsChange}
              onClose={() => setConfiguringSceneId(null)}
            />
          );
        })()}

      {exportDialog?.kind === "shopvibes" && (
        <ShopvibesPushModal
          items={exportDialog.items}
          source="studio"
          onClose={() => setExportDialog(null)}
        />
      )}
      {exportDialog?.kind === "links" && (
        <ImageLinkListModal
          items={exportDialog.items}
          onClose={() => setExportDialog(null)}
        />
      )}

      {measurementImport && (
        <MeasurementImportDialog
          key={measurementImport.sheetName}
          {...measurementImport}
          onSelectSheet={(name) =>
            setMeasurementImport((current) => {
              const sheet = current?.sheets.find((s) => s.sheet === name);
              return current && sheet
                ? { ...current, rows: sheet.data, sheetName: name }
                : current;
            })
          }
          products={studioProducts}
          onSave={saveMeasurements}
          onComplete={(result) =>
            trackProductMeasurementsImportAttempt({ projectId, ...result })
          }
          onClose={() => setMeasurementImport(null)}
        />
      )}

      {excelImport && (
        <ExcelImportErrorDialog
          state={excelImport}
          onClose={() => setExcelImport(null)}
        />
      )}

      {showFileSizeWarning && pendingFile && (
        <FileSizeWarningModal
          fileName={pendingFile.name}
          fileSize={pendingFile.size}
          maxSizeBytes={MAX_FILE_SIZE_BYTES}
          onContinue={() => {
            processNextOversizedFile();
          }}
          onCancel={() => {
            setPendingFile(null);
            setShowFileSizeWarning(false);
            pendingOversizedFilesRef.current = [];
            // A parked mixed-drop spreadsheet still runs — its rows for the
            // canceled images simply show up as unmatched.
            flushPendingSpreadsheet();
          }}
        />
      )}
    </div>
  );
};

export const Route = createFileRoute("/projects/$projectId/")({
  component: StudioView,
});
